import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PlayerShield } from '../components/PlayerShield';
import Hls from 'hls.js';
import { TvPlayerOverlay } from '../tv/TvPlayerOverlay';
import { logTv, isNativeTvWrapper } from '../tv/tvAssets';
import ReportProblemModal from '../components/ReportProblemModal';
import EndScreen from '../components/EndScreen';
import ContentModal from '../components/ContentModal';
import { ProgressPreview } from '../components/ProgressPreview';
import { tel, telSetCtx, telFlush } from '../lib/playerTelemetry';
import { actSid } from '../components/ActivityTracker';
import { isSamsungTv, releaseMedia } from '../lib/tvMedia';
import {
  Play, Pause, Volume2, VolumeX, Volume1,
  Maximize, Minimize, ArrowLeft, Flag,
  ChevronLeft, Check, X,
  Eye, EyeOff, Ear, EarOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// sendBeacon sécurisé : les vieux moteurs TV (webOS) refusent les Blob JSON (SecurityError)
// → repli sur une chaîne text/plain (CORS-safelisted), le backend parse le JSON du corps
const safeBeacon = (url, payload) => {
  try {
    const body = JSON.stringify(payload);
    try {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } catch {
      try { navigator.sendBeacon(url, body); } catch { /* ignore */ }
    }
  } catch { /* ignore */ }
};

/* Custom SVG icons matching Netflix player style */
import {
  Rewind10Icon, Forward10Icon, PrevEpisodeIcon, NextEpisodeIcon, EpisodesListIcon,
  SubtitlesIcon, FiltersIcon, PlayerBtn, AudioSubPanel, EpisodesPanel, FiltersPanel, parseTimestamp,
} from '../components/player/PlayerControls';


export default function VideoPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeProfile, user } = useAuth();
  const profileId = activeProfile?.id || '';
  const freshProfile = user?.profiles?.find(p => p.id === activeProfile?.id) || activeProfile;

  // Persistent player preferences per profile (audio, sub, filters)
  const prefsKey = `player_prefs_${profileId}`;
  const savedPrefs = useRef(null);
  try { savedPrefs.current = JSON.parse(localStorage.getItem(prefsKey)); } catch { savedPrefs.current = null; }

  const savePlayerPrefs = useCallback((audio, sub, playerFilters) => {
    const prefs = { audio, sub, filters: playerFilters, savedAt: Date.now() };
    localStorage.setItem(prefsKey, JSON.stringify(prefs));
  }, [prefsKey]);

  const videoRef = useRef(null);
  const extAudioRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimer = useRef(null);

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const reportTcRef = useRef(0);
  const openReport = () => { reportTcRef.current = videoRef.current?.currentTime || 0; setShowReport(true); };
  const [showVolume, setShowVolume] = useState(false);
  const [showAudioSub, setShowAudioSub] = useState(false);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState(() => (sessionStorage.getItem('tvMode') ? (sessionStorage.getItem('tv_pref_audio') || '__original__') : (savedPrefs.current?.audio || '__original__')));
  const [audioLoading, setAudioLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState(() => ((sessionStorage.getItem('tvMode') && sessionStorage.getItem('tv_pref_sub')) ? sessionStorage.getItem('tv_pref_sub') : (savedPrefs.current?.sub || 'Désactivés')));
  const [filters, setFilters] = useState(() => {
    if (savedPrefs.current?.filters) return savedPrefs.current.filters;
    const pf = freshProfile?.default_filters;
    if (pf && Object.keys(pf).length > 0) return pf;
    return {};
  });
  const [adminFilters, setAdminFilters] = useState([]);
  const filterActionMap = useRef({});
  const adminDefaultsLoaded = useRef(false);
  
  const [showSkipIntro, setShowSkipIntro] = useState(false);
  const [showSkipRecap, setShowSkipRecap] = useState(false);
  const [showNextEp, setShowNextEp] = useState(false);
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [geoCountry, setGeoCountry] = useState('');
  const [showClassification, setShowClassification] = useState(true);
  const [showPauseThumbnail, setShowPauseThumbnail] = useState(false);
  const [fadeToBlack, setFadeToBlack] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [filterAnnouncement, setFilterAnnouncement] = useState(null);
  const filterAnnouncementRef = useRef({});
  const [showFilterWarning, setShowFilterWarning] = useState(true);
  const pauseThumbnailTimer = useRef(null);
  const volumeTimer = useRef(null);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [videoError, setVideoError] = useState(null);

  // ── TV mode (D-pad player) ──
  const tvMode = !!sessionStorage.getItem('tvMode');
  // Sortie TV : une sentinelle d'historique absorbe le history.back() système de webOS
  // (disableBackHistoryAPI:false). La navigation de sortie consomme d'abord la sentinelle
  // puis remplace l'entrée /watch — la pile reste propre, pas de retour fantôme sur /watch.
  const backTsRef = useRef(0);
  const exitTargetRef = useRef(null);
  const guardArmedRef = useRef(false);
  const armGuard = useCallback(() => {
    if (guardArmedRef.current) return;
    try { window.history.pushState({ pGuard: true }, '', window.location.href); guardArmedRef.current = true; } catch { /* ignore */ }
  }, []);
  const tvNavigate = useCallback((to, resume) => {
    try { sessionStorage.setItem('tv_resume', JSON.stringify(resume)); } catch { /* ignore */ }
    if (guardArmedRef.current) {
      exitTargetRef.current = to;
      guardArmedRef.current = false;
      window.history.back();
    } else {
      navigate(to, { replace: true });
    }
  }, [navigate]);
  const tvExit = useCallback(() => {
    logTv('tv_exit_back', { id: content?.id });
    // Retour depuis la lecture → fiche du contenu (pas l'accueil)
    tvNavigate('/tv', { id: content?.id, title: content?.title });
  }, [content, tvNavigate]);
  useEffect(() => {
    if (!tvMode) return;
    armGuard();
    const onPop = () => {
      if (exitTargetRef.current) {
        const to = exitTargetRef.current; exitTargetRef.current = null;
        navigate(to, { replace: true });
        return;
      }
      guardArmedRef.current = false;
      setTimeout(() => { if (!exitTargetRef.current && window.location.pathname.startsWith('/watch')) armGuard(); }, 0);
      // Retour système webOS sans keydown 461 délivré → on rejoue la touche Retour :
      // les modales/panneaux ouverts la consomment, sinon l'overlay déclenche la sortie propre
      if (Date.now() - backTsRef.current < 700) { logTv('pop_dedup', {}); return; }
      logTv('pop_synth_back', {});
      const ev = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true });
      try { Object.defineProperty(ev, 'keyCode', { get: () => 461 }); } catch { /* ignore */ }
      window.dispatchEvent(ev);
    };
    const recordBack = (e) => { if ([27, 8, 461, 10009].includes(e.keyCode) && e.isTrusted) backTsRef.current = Date.now(); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', recordBack, true);
    return () => { window.removeEventListener('popstate', onPop); window.removeEventListener('keydown', recordBack, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Défense webOS : tv_resume posé dès le chargement — même si le retour passe par le
  // history.back() système (handler 461 non délivré), TvApp rouvre la fiche sans rejouer l'intro
  useEffect(() => {
    if (!tvMode || !content?.id) return;
    logTv('player_loaded', { id: content.id, title: content.title });
    try { sessionStorage.setItem('tv_resume', JSON.stringify({ id: content.id, title: content.title, home: true })); } catch { /* ignore */ }
    return () => logTv('player_unmount', { id: content.id });
  }, [tvMode, content]);
  const [pauseAd, setPauseAd] = useState(null);
  useEffect(() => {
    if (!tvMode) return;
    const tryFs = () => { if (!document.fullscreenElement) containerRef.current?.requestFullscreen?.().catch(() => {}); };
    tryFs();
    const onFirstKey = () => { tryFs(); window.removeEventListener('keydown', onFirstKey, true); };
    window.addEventListener('keydown', onFirstKey, true);
    axios.get(`${API}/ads/pause-banner`, { withCredentials: true }).then(r => { if (r.data?.ad) setPauseAd(r.data.ad); }).catch(() => {});
    return () => window.removeEventListener('keydown', onFirstKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Concurrent screens limit (per plan, all devices) ──
  const [streamBlock, setStreamBlock] = useState(null); // {sessions:[{profile_name,device,city,content_title}], max_screens}
  const [streamRetry, setStreamRetry] = useState(0);
  const playSessionId = useRef(null);
  const playRequestRef = useRef(Promise.resolve());
  if (!playSessionId.current) {
    let sid = sessionStorage.getItem('pb_sid');
    if (!sid) {
      sid = window.crypto?.randomUUID?.() || `${Math.random().toString(36).slice(2)}${Date.now()}`;
      sessionStorage.setItem('pb_sid', sid);
    }
    playSessionId.current = sid;
  }
  useEffect(() => {
    if (!content) return;
    let hb;
    let cancelled = false;
    const start = async () => {
      try {
        await axios.post(`${API}/playback/start`, {
          session_id: playSessionId.current, content_id: id,
          episode_id: currentEpisode?.id || '', profile_id: freshProfile?.id || '',
          device_hint: tvMode ? 'tv' : '',
        }, { withCredentials: true });
        if (cancelled) return;
        setStreamBlock(null);
        telSetCtx({ content_id: id, session_id: actSid() });
        tel('start', videoRef.current?.currentTime || 0);
        hb = setInterval(() => {
          axios.post(`${API}/playback/heartbeat`, {
            session_id: playSessionId.current, content_id: id,
            position: Math.round(videoRef.current?.currentTime || 0),
            paused: !!videoRef.current?.paused,
            rate: videoRef.current?.playbackRate || 1,
            filters: filtersTelRef.current || [],
          }, { withCredentials: true })
            .then(r => {
              if (cancelled) return;
              if (r.data?.revoked) {
                videoRef.current?.pause();
                if (r.data?.reason === 'subscription_required') setStreamBlock({ subscription_required: true });
                else setStreamBlock({ remote_disconnect: true });
              }
            })
            .catch(err => {
              if (cancelled) return;
              // Device remotely disconnected: auth is gone -> stop playback with the same message
              if (err.response?.status === 401) {
                videoRef.current?.pause();
                setStreamBlock({ remote_disconnect: true });
              }
            });
        }, 30000);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 409) setStreamBlock(e.response.data);
        else if (e.response?.status === 451) setStreamBlock({ geo_blocked: true, country: e.response.data?.country || '' });
        else if (e.response?.status === 402) setStreamBlock({ subscription_required: true });
      }
    };
    // Serialize retry/replacement with the previous stop. A late stop must not
    // delete the newly started session using the same per-tab session ID.
    playRequestRef.current = playRequestRef.current.catch(() => {}).then(start);
    const stopBeacon = () => {
      safeBeacon(`${API}/playback/stop`, { session_id: playSessionId.current });
    };
    window.addEventListener('pagehide', stopBeacon);
    return () => {
      cancelled = true;
      clearInterval(hb);
      window.removeEventListener('pagehide', stopBeacon);
      playRequestRef.current = playRequestRef.current.catch(() => {}).then(() =>
        axios.post(`${API}/playback/stop`, { session_id: playSessionId.current }, { withCredentials: true }).catch(() => {}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id, streamRetry]);
  // Blocked: make sure nothing plays + TV remote support (D-pad selects Réessayer/Retour, Back = quit)
  const [blockBtn, setBlockBtn] = useState(0); // 0 = retry, 1 = back
  const blockBtnRef = useRef(0);
  useEffect(() => { blockBtnRef.current = blockBtn; }, [blockBtn]);
  useEffect(() => {
    if (!streamBlock) return;
    setBlockBtn((streamBlock.geo_blocked || streamBlock.subscription_required) ? 1 : 0);
    hasResumed.current = false; // re-fetch resume position once unblocked
    videoRef.current?.pause();
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault(); e.stopPropagation();
        if (streamBlock.geo_blocked || streamBlock.subscription_required) return;
        setBlockBtn(b => (e.key === 'ArrowLeft' ? Math.max(0, b - 1) : Math.min(1, b + 1)));
      } else if (e.key === 'Enter') {
        e.preventDefault(); e.stopPropagation();
        if (streamBlock.geo_blocked || streamBlock.subscription_required || blockBtnRef.current === 1) { if (tvMode) tvExit(); else navigate(-1); }
        else setStreamRetry(x => x + 1);
      } else if (e.key === 'Escape' || e.key === 'Backspace' || e.keyCode === 10009 || e.keyCode === 461) {
        e.preventDefault(); e.stopPropagation(); if (tvMode) tvExit(); else navigate(-1);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamBlock]);

  // ── Ad player state ──
  const [adState, setAdState] = useState('loading'); // loading, pre-roll, playing, mid-roll
  const adPhaseRef = useRef(adState);
  adPhaseRef.current = adState;
  const adDecisionKeyRef = useRef(null);
  const [samsungReadyKey, setSamsungReadyKey] = useState(null);
  const [adQueue, setAdQueue] = useState([]);
  const [currentAd, setCurrentAd] = useState(null);
  const [adTimeLeft, setAdTimeLeft] = useState(0);
  const [adPlaying, setAdPlaying] = useState(true);
  const [adMuted, setAdMuted] = useState(false);
  const [hasAds, setHasAds] = useState(false);
  const [midRollAds, setMidRollAds] = useState([]);
  const adVideoRef = useRef(null);
  const midRollTimers = useRef([]);
  const midRollTriggered = useRef(new Set());

  // ── Pistes audio / sous-titres : l'épisode en cours prime (le niveau série ne concerne que la bande-annonce) ──
  const activeMedia = useMemo(() => {
    if (!content || !currentEpisode) return content;
    return {
      ...content,
      audio_tracks: currentEpisode.audio_tracks || [],
      subtitles: currentEpisode.subtitles || [],
      burned_subtitles_lang: currentEpisode.burned_subtitles_lang || '',
    };
  }, [content, currentEpisode]);

  // ── Fin de série / épisode suivant automatique ──
  const [endScreen, setEndScreen] = useState(null); // null | 'offer' | 'trailer' | 'poster'
  const [similar, setSimilar] = useState(null);
  const [moreInfoId, setMoreInfoId] = useState(null);
  const [nextEpCountdown, setNextEpCountdown] = useState(null);
  const endTriggeredRef = useRef(false);
  const autoNextRef = useRef(null);
  const autoNextCancelRef = useRef(null);
  const endScreenResumedRef = useRef(false);
  const [creditsEnded, setCreditsEnded] = useState(false);
  useEffect(() => {
    setEndScreen(null); setSimilar(null); setMoreInfoId(null); setNextEpCountdown(null);
    endTriggeredRef.current = false; autoNextRef.current = null; autoNextCancelRef.current = null;
    endScreenResumedRef.current = false;
    setCreditsEnded(false);
    setCurrentEpisode(null); setVideoError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Intro logo Maya+ (bumper joué côté lecteur avant la vidéo, hors timecode) ──
  const [introBumpers, setIntroBumpers] = useState(null);
  const [introPlaying, setIntroPlaying] = useState(false);
  const introActiveRef = useRef(false);
  const introVideoRef = useRef(null);
  useEffect(() => {
    axios.get(`${API}/intros`, { timeout: 8000 }).then(r => setIntroBumpers(r.data || {})).catch(() => setIntroBumpers({}));
  }, []);
  const introKind = (content?.intro_bumper === 'originals' || content?.intro_bumper === 'normal') ? content.intro_bumper : null;
  const introSrc = introKind ? ((introKind === 'originals' ? introBumpers?.originals_url : introBumpers?.normal_url) || null) : null;

  // L'intro ne se joue que si la lecture démarre du tout début (jamais en reprise),
  // puis à chaque nouvel épisode lancé au début, et au retour tout en arrière.
  const introDecisionKeyRef = useRef(null);
  useEffect(() => {
    if (!content || streamBlock) return;
    if (!introKind) { introActiveRef.current = false; setIntroPlaying(false); return; }
    const key = `${content.id}:${currentEpisode?.id || ''}`;
    if (introDecisionKeyRef.current === key) return; // idempotent (re-render / StrictMode)
    const sameContent = (introDecisionKeyRef.current || '').split(':')[0] === content.id;
    introDecisionKeyRef.current = key;

    // Reprise différée sur un épisode sauvegardé → pas d'intro
    if (pendingSeekRef.current != null) { introActiveRef.current = false; setIntroPlaying(false); return; }
    // Changement d'épisode manuel (démarre au début) → intro
    if (sameContent) { introActiveRef.current = true; setIntroPlaying(true); return; }

    // Premier lancement de ce contenu : on vérifie s'il existe une reprise en cours
    introActiveRef.current = true; // bloque l'autoplay du film le temps de la décision
    axios.get(`${API}/watch-progress/${id}`, { params: { profile_id: profileId }, withCredentials: true, timeout: 10000 })
      .then(({ data }) => {
        if (introDecisionKeyRef.current !== key) return; // une décision plus récente a pris le relais
        // Un épisode explicitement demandé différent de la reprise sauvegardée démarre au début
        const epParam = new URLSearchParams(window.location.search).get('episode');
        const freshEpisode = !!(epParam && data.episode_id && epParam !== data.episode_id);
        const resuming = !freshEpisode && data.current_time > 5 && data.progress > 5 && data.progress < 95;
        if (resuming) {
          // Reprise en cours de lecture → pas d'intro, on libère le film
          introActiveRef.current = false;
          setIntroPlaying(false);
          if (isSamsungTv && adPhaseRef.current === 'done' && adDecisionKeyRef.current === key) setSamsungReadyKey(key);
          if (videoRef.current && videoRef.current.paused && videoRef.current.readyState >= 3 && adState === 'done') {
            videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
          }
        } else {
          setIntroPlaying(true);
        }
      })
      .catch(() => { if (introDecisionKeyRef.current === key) setIntroPlaying(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, currentEpisode, streamBlock]);

  const endIntro = useCallback(() => {
    if (isSamsungTv) releaseMedia(introVideoRef.current);
    introActiveRef.current = false;
    setIntroPlaying(false);
    if (videoRef.current && adState === 'done') {
      videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adState]);

  // Sécurité : si l'intro ne démarre jamais (réseau), on passe au film après 12 s
  useEffect(() => {
    if (!introPlaying || adState !== 'done') return;
    const t = setTimeout(endIntro, 12000);
    return () => clearTimeout(t);
  }, [introPlaying, endIntro, adState]);

  // Si les pubs se résolvent en "aucune pub" après la fin de l'intro, lance le film
  useEffect(() => {
    if (adState === 'done' && !introActiveRef.current && videoRef.current && videoRef.current.paused && !videoError) {
      videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adState]);
  const adPlayDuration = useRef(0);
  const adEndedHandledRef = useRef(false);
  const lastMidRollCheckRef = useRef(null);
  const [adTotalCount, setAdTotalCount] = useState(0);
  const [adCurrentIndex, setAdCurrentIndex] = useState(0);
  const [showAdVolume, setShowAdVolume] = useState(false);
  const [adVolume, setAdVolume] = useState(1);
  const adVolumeTimer = useRef(null);

  // Télémétrie filtres : journalise chaque activation/désactivation + état courant pour le live
  const filtersTelRef = useRef(null);
  useEffect(() => {
    const active = Object.keys(filters).filter(k => filters[k]);
    const prev = filtersTelRef.current;
    filtersTelRef.current = active;
    if (prev == null) return;
    active.filter(k => !prev.includes(k)).forEach(k => tel('filter_on', videoRef.current?.currentTime || 0, { filter: k }));
    prev.filter(k => !active.includes(k)).forEach(k => tel('filter_off', videoRef.current?.currentTime || 0, { filter: k }));
  }, [filters]);

  // Save player prefs when audio, sub, or filters change
  useEffect(() => {
    savePlayerPrefs(selectedAudio, selectedSub, filters);
  }, [selectedAudio, selectedSub, filters, savePlayerPrefs]);

  // Normalize stale prefs: fall back to original audio / subs off if selection unavailable on this content
  useEffect(() => {
    if (!activeMedia) return;
    const tracks = activeMedia.audio_tracks || [];
    if (selectedAudio !== '__original__' && !tracks.some(t => t.language === selectedAudio)) {
      setSelectedAudio('__original__');
    }
    const subs = activeMedia.subtitles || [];
    if (selectedSub !== 'Désactivés' && !subs.some(s => s.language === selectedSub)) {
      setSelectedSub('Désactivés');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMedia]);

  // Fetch dynamic admin filters and build defaults + action map
  useEffect(() => {
    axios.get(`${API}/filters`, { withCredentials: true })
      .then(r => {
        const fetched = r.data || [];
        setAdminFilters(fetched);
        const actionMap = {};
        const dynamicDefaults = {};
        fetched.forEach(f => {
          actionMap[f.id] = f.action || 'skip';
          dynamicDefaults[f.id] = f.id !== 'violence';
        });
        filterActionMap.current = actionMap;
        adminDefaultsLoaded.current = true;
        // Always merge: dynamicDefaults as base → profile defaults → saved prefs (if any) on top
        const pf = freshProfile?.default_filters || {};
        const sp = savedPrefs.current?.filters || {};
        const hasLocalOverride = savedPrefs.current?.filters;
        setFilters({ ...dynamicDefaults, ...pf, ...(hasLocalOverride ? sp : {}) });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When profile default_filters change (user edited in settings), apply them and clear stale cache
  const prevProfileFilters = useRef(JSON.stringify(freshProfile?.default_filters || {}));
  useEffect(() => {
    const pf = freshProfile?.default_filters;
    if (!pf || Object.keys(pf).length === 0) return;
    const pfStr = JSON.stringify(pf);
    // Skip if profile defaults haven't actually changed
    if (pfStr === prevProfileFilters.current) return;
    prevProfileFilters.current = pfStr;
    // Profile defaults changed — merge with admin defaults base layer, clear player cache
    setFilters(prev => {
      // Keep admin defaults as base, then apply new profile defaults on top
      const base = {};
      if (adminDefaultsLoaded.current) {
        // Preserve admin default keys that are not in profile defaults
        Object.keys(prev).forEach(k => { base[k] = prev[k]; });
      }
      return { ...base, ...pf };
    });
    localStorage.removeItem(prefsKey);
    savedPrefs.current = null;
  }, [freshProfile?.default_filters, prefsKey]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data } = await axios.get(`${API}/contents/${id}`, { withCredentials: true });
        setContent(data);
        // For series, select the episode from the URL (?episode=) or default to the first
        if (data.type === 'series' && data.episodes?.length > 0) {
          const epParam = new URLSearchParams(window.location.search).get('episode');
          const target = epParam ? data.episodes.find(e => e.id === epParam) : null;
          if (target) setCurrentEpisode(target);
          else if (!data.video_url) setCurrentEpisode(data.episodes[0]);
        }
        // Check geo-restriction (skip for admins)
        if (!data.geo_all_countries && data.geo_countries?.length > 0 && user?.role !== 'admin') {
          try {
            const { data: access } = await axios.get(`${API}/contents/${id}/access`, { withCredentials: true });
            if (!access.accessible) { setGeoBlocked(true); setGeoCountry(access.country); }
          } catch {}
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchContent();
  }, [id]);

  // Pre-roll selection uses the plan and slot, not the decoder's duration.
  // Requiring loadedmetadata here deadlocks devices with a single video decoder.
  useEffect(() => {
    if (!content) return;
    const key = `${content.id}:${currentEpisode?.id || ''}`;
    let cancelled = false;
    adDecisionKeyRef.current = null;
    setAdState('loading');
    setCurrentAd(null);
    setAdQueue([]);
    midRollTriggered.current = new Set();
    lastMidRollCheckRef.current = null;
    const fetchAds = async () => {
      try {
        const { data: preRoll } = await axios.get(`${API}/ads/next`, { params: { position: 'pre-roll' }, withCredentials: true, timeout: 10000 });
        if (cancelled) return;
        adDecisionKeyRef.current = key;
        if (!preRoll.has_ads) { setAdState('done'); setHasAds(false); return; }
        setHasAds(true);
        if (preRoll.ads?.length > 0) {
          setAdQueue(preRoll.ads);
          setAdTotalCount(preRoll.ads.length);
          setAdCurrentIndex(1);
          setAdState('pre-roll');
        } else {
          setAdState('done');
        }
      } catch {
        if (cancelled) return;
        adDecisionKeyRef.current = key;
        setAdState('done'); setHasAds(false);
      }
    };
    fetchAds();
    return () => { cancelled = true; };
  }, [content, currentEpisode?.id]);

  // Samsung: acquire the main decoder only AFTER pre-roll and the intro.
  // Sticky per media, so a mid-roll/pause never resets the source or progress.
  useEffect(() => {
    if (!isSamsungTv || !content || adState !== 'done' || introPlaying || introActiveRef.current) return;
    const key = `${content.id}:${currentEpisode?.id || ''}`;
    if (adDecisionKeyRef.current === key) setSamsungReadyKey(key);
  }, [content, currentEpisode?.id, adState, introPlaying]);

  // Calculate mid-roll break points based on content duration
  const getMidRollBreakpoints = useCallback(() => {
    if (!duration || duration <= 0) return [];
    const durationMin = duration / 60;
    let count = 0;
    if (durationMin >= 30 && durationMin < 60) count = 1;
    else if (durationMin >= 60 && durationMin < 90) count = 2;
    else if (durationMin >= 90) count = 3;
    const points = [];
    const interval = duration / (count + 1);
    const tenMinBefore = duration - 600; // no ads in last 10 min
    for (let i = 1; i <= count; i++) {
      const t = Math.round(interval * i);
      if (t < tenMinBefore) points.push(t);
    }
    return points;
  }, [duration]);

  // Trigger mid-roll ads at breakpoints
  useEffect(() => {
    if (adState !== 'done') return;
    if (!hasAds || !playing) return;
    const breakpoints = getMidRollBreakpoints();
    if (breakpoints.length === 0) return;
    const checkMidRoll = () => {
      if (!videoRef.current) return;
      const t = videoRef.current.currentTime;
      const prev = lastMidRollCheckRef.current;
      lastMidRollCheckRef.current = t;
      for (const bp of breakpoints) {
        // Déclenche au passage naturel OU quand un seek saute par-dessus le marqueur
        const crossed = (t >= bp && t < bp + 2) || (prev != null && prev < bp && t >= bp);
        if (crossed && !midRollTriggered.current.has(bp)) {
          midRollTriggered.current.add(bp);
          videoRef.current.pause();
          setPlaying(false);
          // Fetch fresh mid-roll ads
          const durationMin = Math.round(duration / 60);
          axios.get(`${API}/ads/next`, { params: { content_duration: durationMin, position: 'mid-roll' }, withCredentials: true })
            .then(({ data }) => {
              if (data.ads?.length > 0) {
                setAdQueue(data.ads);
                setAdTotalCount(data.ads.length);
                setAdCurrentIndex(1);
                setAdState('mid-roll');
              }
            }).catch(() => {
              videoRef.current?.play().catch(() => {});
              setPlaying(true);
            });
          break;
        }
      }
    };
    const interval = setInterval(checkMidRoll, 500);
    return () => clearInterval(interval);
  }, [adState, hasAds, playing, getMidRollBreakpoints, duration]);

  // Play current ad from queue (la pub pré-roll passe AVANT l'intro logo Maya+)
  useEffect(() => {
    if ((adState !== 'pre-roll' && adState !== 'mid-roll') || adQueue.length === 0) return;
    const ad = adQueue[0];
    const playDur = ad.play_duration || ad.duration || 15;
    adEndedHandledRef.current = false;
    setCurrentAd(ad);
    setAdTimeLeft(playDur);
    adPlayDuration.current = playDur;
    setAdPlaying(true);
    setAdMuted(false);
    // Pause main video during ad
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setPlaying(false);
    }
    // Track impression
    axios.post(`${API}/ads/${ad.id}/impression`).catch(() => {});
    tel('ad_view', videoRef.current?.currentTime || 0, { ad_id: ad.id, ad: ad.title || ad.name || '', slot: adState });
  }, [adState, adQueue]);

  const onAdEnded = useCallback(() => {
    if (adEndedHandledRef.current) return; // évite le double comptage (timeupdate + onEnded)
    adEndedHandledRef.current = true;
    const done = adQueue[0];
    if (done) tel('ad_end', videoRef.current?.currentTime || 0, { ad_id: done.id, ad: done.title || done.name || '' });
    const remaining = adQueue.slice(1);
    if (remaining.length > 0) {
      setAdQueue(remaining);
      setAdCurrentIndex(prev => prev + 1);
      // Force reload the ad video with next ad
      setTimeout(() => {
        if (adVideoRef.current) {
          adVideoRef.current.load();
          adVideoRef.current.play().catch(() => {});
          setAdPlaying(true);
        }
      }, 100);
    } else {
      if (isSamsungTv) releaseMedia(adVideoRef.current);
      setCurrentAd(null);
      setAdQueue([]);
      setAdState('done');
      // Si l'intro logo doit encore se jouer (pré-roll avant intro), on ne lance pas le film ici
      if (videoRef.current && !introActiveRef.current) {
        videoRef.current.play().catch(() => {});
        setPlaying(true);
      }
    }
  }, [adQueue]);

  // Sync ad timer with real video time + enforce play_duration limit
  useEffect(() => {
    if (!currentAd || !adVideoRef.current) return;
    const adVideo = adVideoRef.current;
    const onTimeUpdate = () => {
      const t = adVideo.currentTime;
      const maxDur = adPlayDuration.current;
      // Calculate total remaining: current ad remaining + all queued ads durations
      const currentAdLeft = Math.max(0, maxDur - t);
      const queuedLeft = adQueue.slice(1).reduce((sum, a) => sum + (a.play_duration || a.duration || 15), 0);
      setAdTimeLeft(Math.ceil(currentAdLeft + queuedLeft));
      if (t >= maxDur) {
        adVideo.pause();
        onAdEnded();
      }
    };
    adVideo.addEventListener('timeupdate', onTimeUpdate);
    return () => adVideo.removeEventListener('timeupdate', onTimeUpdate);
  }, [currentAd, onAdEnded, adQueue]);

  const onAdClick = useCallback(() => {
    if (!currentAd?.click_url) return;
    tel('ad_click', videoRef.current?.currentTime || 0, { ad_id: currentAd.id, ad: currentAd.title || currentAd.name || '', slot: adState });
    axios.post(`${API}/ads/${currentAd.id}/click`).catch(() => {});
    let url = currentAd.click_url;
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    window.open(url, '_blank');
  }, [currentAd, adState]);

  // Classification overlay: show for 5s then fade out (also triggers filter warning)
  useEffect(() => {
    if (!content) return;
    setShowClassification(true);
    setShowFilterWarning(true);
    const timer = setTimeout(() => { setShowClassification(false); setShowFilterWarning(false); }, 5000);
    return () => clearTimeout(timer);
  }, [content]);

  // HLS setup — reacts to content and episode changes
  const hlsRef = useRef(null);
  const muxActiveRef = useRef(false);
  const [srcEpoch, setSrcEpoch] = useState(0);
  useEffect(() => {
    if (!content || !videoRef.current || streamBlock) return;
    if (isSamsungTv && samsungReadyKey !== `${content.id}:${currentEpisode?.id || ''}`) return;
    const video = videoRef.current;
    muxActiveRef.current = false;
    const ep = currentEpisode;
    const srcContent = ep || content;
    const hlsReady = srcContent.hls_url && srcContent.transcode_status === 'COMPLETE';
    const originalSrc = srcContent.video_url || content.video_url || '';
    // A QuickTime source is not a compatible fallback for an already encoded Samsung stream.
    const mp4Src = isSamsungTv && hlsReady && /\.mov(?:[?#]|$)/i.test(originalSrc) ? '' : originalSrc;
    const videoSrc = hlsReady ? srcContent.hls_url : originalSrc;

    if (!videoSrc) return;
    if (tvMode) logTv('video_src', { id: srcContent.id, hlsReady: !!hlsReady, kind: videoSrc.includes('.m3u8') ? 'hls' : 'mp4',
      mse: Hls.isSupported(), src: String(videoSrc).slice(0, 160) });

    // Cleanup previous HLS instance
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    // Reset player state
    video.pause();
    setCurrentTime(0);
    setShowSkipIntro(false);
    setShowNextEp(false);
    setVideoError(null);

    const loadMp4 = (src) => {
      video.removeAttribute('crossOrigin');
      video.src = src;
      video.load();
      if (adState !== 'pre-roll' && adState !== 'mid-roll' && !introActiveRef.current) {
        video.play().then(() => setPlaying(true)).catch(() => {});
      }
    };

    // ── Chaîne de secours TV (Fire TV/webview) : hls.js → HLS natif (manifests proxy) → MP4 ──
    const stageRef = { s: videoSrc.includes('.m3u8') ? 'hls' : 'mp4' };
    let observedPlaying = false;
    const sourceStartedAt = Date.now();
    let wdTimer = null;
    const tryNativeHls = () => {
      stageRef.s = 'native';
      if (tvMode) logTv('video_fallback_native_hls', { id: srcContent.id });
      video.removeAttribute('crossOrigin');
      video.src = videoSrc;
      video.load();
      if (adState !== 'pre-roll' && adState !== 'mid-roll' && !introActiveRef.current) {
        video.play().then(() => setPlaying(true)).catch(() => {});
      }
    };
    const fallbackFromHls = () => {
      // Sur TV, le lecteur natif sait lire les manifests réécrits du proxy HLS (URLs signées absolues)
      if (tvMode && (isNativeTvWrapper || video.canPlayType('application/vnd.apple.mpegurl'))) { tryNativeHls(); armWatchdog(); return; }
      if (mp4Src && mp4Src !== videoSrc) {
        stageRef.s = 'mp4';
        if (tvMode) logTv('video_fallback_mp4', { id: srcContent.id });
        loadMp4(mp4Src);
        armWatchdog();
      } else {
        setVideoError('Impossible de charger la vidéo');
      }
    };
    // Watchdog : si aucune image après 12 s alors que la lecture est lancée, on change de lecteur
    const armWatchdog = () => {
      if (!tvMode) return;
      clearTimeout(wdTimer);
      wdTimer = setTimeout(() => {
        const v = videoRef.current;
        if (!v) return;
        if (observedPlaying && v.currentTime > 0.3) { logTv('video_ok', { id: srcContent.id, stage: stageRef.s }); return; }
        if (introActiveRef.current || ['loading', 'pre-roll', 'mid-roll'].includes(adPhaseRef.current) || document.hidden) { armWatchdog(); return; }
        if (v.paused && observedPlaying) return; // User pause, not a failed startup.
        // hls.js allows 20s per fragment request. Do not destroy a valid stream
        // at 12s while its first request/retry is still in flight.
        if (stageRef.s === 'hls' && hlsRef.current && Date.now() - sourceStartedAt < 45000) {
          armWatchdog(); return;
        }
        logTv('video_watchdog_stall', { id: srcContent.id, stage: stageRef.s, rs: v.readyState });
        if (stageRef.s === 'hls') {
          if (hlsRef.current) { try { hlsRef.current.destroy(); } catch { /* ignore */ } hlsRef.current = null; }
          fallbackFromHls();
        } else if (stageRef.s === 'native' && mp4Src && mp4Src !== videoSrc) {
          stageRef.s = 'mp4';
          logTv('video_fallback_mp4', { id: srcContent.id });
          loadMp4(mp4Src);
          armWatchdog();
        } else {
          setVideoError('Impossible de lire la vidéo sur cet appareil');
        }
      }, 12000);
    };
    const onVElErr = () => {
      if (tvMode) logTv('video_el_error', { id: srcContent.id, stage: stageRef.s, code: video.error && video.error.code });
      // Échec du lecteur natif → dernier recours MP4
      if (stageRef.s === 'native' && mp4Src && mp4Src !== videoSrc) { stageRef.s = 'mp4'; loadMp4(mp4Src); armWatchdog(); }
    };
    const onVPlaying = () => {
      observedPlaying = true;
      if (tvMode) logTv('video_playing', { id: srcContent.id, stage: stageRef.s });
      video.removeEventListener('playing', onVPlaying);
    };
    const onVMetadata = () => {
      if (tvMode) logTv('video_metadata', { id: srcContent.id, stage: stageRef.s, duration: video.duration, ready: video.readyState });
    };
    const onVStalled = () => {
      if (tvMode) logTv('video_waiting', { id: srcContent.id, stage: stageRef.s, ready: video.readyState, paused: video.paused });
    };
    if (tvMode) {
      video.addEventListener('error', onVElErr);
      video.addEventListener('playing', onVPlaying);
      video.addEventListener('loadedmetadata', onVMetadata);
      video.addEventListener('stalled', onVStalled);
    }
    const cleanup = () => {
      clearTimeout(wdTimer);
      video.removeEventListener('error', onVElErr);
      video.removeEventListener('playing', onVPlaying);
      video.removeEventListener('loadedmetadata', onVMetadata);
      video.removeEventListener('stalled', onVStalled);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      if (isSamsungTv) releaseMedia(video);
    };

    if (videoSrc.includes('.m3u8') && Hls.isSupported()) {
      const cfSig = videoSrc.includes('Key-Pair-Id') ? videoSrc.split('?')[1] : '';
      const hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: true,
        enableWorker: !(isNativeTvWrapper || isSamsungTv),
        abrEwmaDefaultEstimate: 4000000,
        maxBufferLength: 30,
        backBufferLength: 30,
        startFragPrefetch: true,
        manifestLoadingTimeOut: 8000,
        manifestLoadingMaxRetry: 2,
        fragLoadingMaxRetry: 4,
        xhrSetup: cfSig ? (xhr, url) => {
          if (url.includes('cloudfront.net') && !url.includes('Key-Pair-Id')) {
            xhr.open('GET', url + (url.includes('?') ? '&' : '?') + cfSig, true);
          }
        } : undefined,
      });
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (tvMode) logTv('video_manifest', { id: srcContent.id, levels: hls.levels.length });
        if (adState !== 'pre-roll' && adState !== 'mid-roll' && !introActiveRef.current) {
          video.play().then(() => setPlaying(true)).catch(() => {});
        }
      });
      // Récupération d'erreurs : réseau → relance, décodage → recover, sinon bascule HLS natif (TV) puis MP4
      let hlsFailed = false;
      let netRetries = 0, mediaRetries = 0;
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (hlsFailed || !data.fatal) return;
        if (tvMode) logTv('hls_fatal', { id: srcContent.id, type: data.type, details: data.details, net: netRetries, med: mediaRetries });
        const manifestErr = data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT;
        if (!manifestErr && data.type === Hls.ErrorTypes.NETWORK_ERROR && netRetries < 2) { netRetries++; hls.startLoad(); return; }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < 2) {
          mediaRetries++;
          if (mediaRetries === 2) hls.swapAudioCodec();
          hls.recoverMediaError();
          return;
        }
        hlsFailed = true;
        console.warn('HLS failed, fallback:', data.details);
        hls.detachMedia();
        hls.destroy();
        hlsRef.current = null;
        fallbackFromHls();
      });
      hlsRef.current = hls;
      armWatchdog();

      return cleanup;
    } else if (videoSrc.includes('.m3u8') && (isNativeTvWrapper || video.canPlayType('application/vnd.apple.mpegurl'))) {
      stageRef.s = 'native';
      video.src = videoSrc;
      if (adState !== 'pre-roll' && adState !== 'mid-roll' && !introActiveRef.current) {
        video.play().then(() => setPlaying(true)).catch(() => {});
      }
      armWatchdog();
    } else {
      loadMp4(videoSrc);
      armWatchdog();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, currentEpisode, streamBlock, srcEpoch, samsungReadyKey]);

  // Resume from saved position (including the right episode for series)
  const hasResumed = useRef(false);
  const resumeToast = null;
  const showResumeToast = () => {}; // affichage « Reprise à … » désactivé à la demande
  const pendingSeekRef = useRef(null);
  const currentEpisodeRef = useRef(null);
  useEffect(() => { currentEpisodeRef.current = currentEpisode; }, [currentEpisode]);

  // Image gelée pendant un changement de source (piste audio) : aucune coupure visuelle
  // TV : drawImage(video) est bloqué par le décodeur matériel (LG webOS) → on fige via la sprite de scène
  const freezeCanvasRef = useRef(null);
  const [freezeOn, setFreezeOn] = useState(false);
  const [freezeSprite, setFreezeSprite] = useState(null);
  const freezeFrame = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime || 0;
    const ep = currentEpisodeRef.current;
    const sUrl = ep?.sprite_url || content?.sprite_url || null;
    const sMeta = (ep?.sprite_url ? ep?.sprite_meta : content?.sprite_meta) || null;
    if (tvMode && sUrl && sMeta && sMeta.cols) {
      const cols = sMeta.cols || 1, rows = sMeta.rows || 1;
      const count = sMeta.count || cols * rows;
      const idx = Math.max(0, Math.min(count - 1, Math.floor(t / (sMeta.interval || 10))));
      const col = idx % cols, row = Math.floor(idx / cols);
      setFreezeSprite({
        url: sUrl,
        size: `${cols * 100}% ${rows * 100}%`,
        pos: `${cols > 1 ? (col / (cols - 1)) * 100 : 0}% ${rows > 1 ? (row / (rows - 1)) * 100 : 0}%`,
        blur: true,
      });
      setFreezeOn(true);
    } else if (tvMode && (content?.freeze_frame_url || content?.backdrop_url || content?.thumbnail_url || content?.poster_url)) {
      setFreezeSprite({ url: content.freeze_frame_url || content.backdrop_url || content.thumbnail_url || content.poster_url, size: 'cover', pos: 'center', blur: false });
      setFreezeOn(true);
    } else {
      const c = freezeCanvasRef.current;
      if (!c || !v.videoWidth) return;
      try {
        c.width = v.videoWidth; c.height = v.videoHeight;
        c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
        setFreezeSprite(null);
        setFreezeOn(true);
      } catch { setFreezeOn(false); return; }
    }
    v.addEventListener('playing', () => setTimeout(() => setFreezeOn(false), 150), { once: true });
    setTimeout(() => setFreezeOn(false), 6000);
  }, [content, tvMode]);

  useEffect(() => {
    if (!content || hasResumed.current || streamBlock) return;
    const fetchProgress = async () => {
      try {
        const { data } = await axios.get(`${API}/watch-progress/${id}`, { params: { profile_id: profileId }, withCredentials: true });
        // Restore the audio/subtitle language this content was left in (per profile)
        if (data.audio_lang) {
          const originalLang = (content.language || '').toLowerCase();
          const saved = data.audio_lang;
          const tracks = content.audio_tracks || [];
          const epTracks = (content.episodes || []).flatMap(e => e.audio_tracks || []);
          const isExternal = [...tracks, ...epTracks].some(t => t.language === saved);
          if (isExternal) setSelectedAudio(saved);
          else if (saved.toLowerCase() === originalLang || saved === 'Original') setSelectedAudio('__original__');
          setSelectedSub(data.subtitle_lang || 'Désactivés');
        }
        if (data.current_time > 5 && data.progress > 5 && data.progress < 95) {
          const epParam = new URLSearchParams(window.location.search).get('episode');
          const eps = content.episodes || [];
          const savedEp = data.episode_id ? eps.find(e => e.id === data.episode_id) : null;
          // An explicitly requested episode different from the saved one starts fresh
          if (epParam && data.episode_id && epParam !== data.episode_id) return;
          hasResumed.current = true;
          const resumeAt = data.current_time;
          if (savedEp && savedEp.id !== currentEpisodeRef.current?.id) {
            // Switch to the saved episode; seek applied once its metadata loads
            pendingSeekRef.current = resumeAt;
            setCurrentEpisode(savedEp);
            return;
          }
          // Wait for video to be ready
          const trySeek = () => {
            if (videoRef.current && videoRef.current.readyState >= 1) {
              videoRef.current.currentTime = resumeAt;
              showResumeToast(resumeAt);
            } else {
              setTimeout(trySeek, 200);
            }
          };
          trySeek();
        }
      } catch {}
    };
    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, id, streamBlock]);

  // Save progress periodically
  useEffect(() => {
    if (!content || !playing) return;
    const saveProgress = () => {
      if (videoRef.current && duration > 0) {
        const ct = videoRef.current.currentTime;
        const activeM = currentEpisodeRef.current || content;
        const payload = {
          content_id: id,
          profile_id: profileId,
          progress: Math.round((ct / duration) * 100),
          current_time: Math.round(ct),
          total_duration: Math.round(duration),
          episode_id: currentEpisodeRef.current?.id || undefined,
          audio_lang: selectedAudio === '__original__' ? (activeM?.language || content?.language || 'Original') : selectedAudio,
          subtitle_lang: selectedSub === 'Désactivés' ? null : selectedSub,
          watch_delta: 15,
        };
        axios.post(`${API}/watch-history`, payload, { withCredentials: true }).catch(() => {});
      }
    };
    const interval = setInterval(saveProgress, 15000);
    return () => clearInterval(interval);
  }, [content, playing, duration, id, selectedAudio, selectedSub]);

  // Persiste immédiatement le choix de langue (sinon une reprise restaure l'ancienne langue)
  const audioLangInit = useRef(true);
  useEffect(() => {
    if (audioLangInit.current) { audioLangInit.current = false; return; }
    const v = videoRef.current;
    if (!content || !v || v.currentTime < 5) return;
    const activeM = currentEpisodeRef.current || content;
    axios.post(`${API}/watch-history`, {
      content_id: id, profile_id: profileId,
      progress: duration > 0 ? Math.round((v.currentTime / duration) * 100) : 0,
      current_time: Math.round(v.currentTime), total_duration: Math.round(duration || 0),
      episode_id: currentEpisodeRef.current?.id || undefined,
      audio_lang: selectedAudio === '__original__' ? (activeM?.language || content?.language || 'Original') : selectedAudio,
      subtitle_lang: selectedSub === 'Désactivés' ? null : selectedSub,
      watch_delta: 0,
    }, { withCredentials: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudio]);

  // Préchauffage TV : s'assure que la piste opposée est prête (mux) et met en cache ses premiers Mo
  // (+ la source d'origine quand on joue le mux) → bascule de langue quasi instantanée
  const warmedRef = useRef({});
  useEffect(() => {
    if (!tvMode || !content) return;
    const activeM = currentEpisode || content;
    const tracks = activeM?.audio_tracks || [];
    if (!tracks.length) return;
    const kind = currentEpisode ? 'episode' : 'content';
    const mid = currentEpisode?.id || content?.id;
    const warm = (url) => {
      fetch(url, { headers: { Range: 'bytes=0-2097151' }, cache: 'force-cache' }).catch(() => {});
    };
    const t = setTimeout(async () => {
      for (const tr of tracks) {
        if (tr.language === selectedAudio) continue;
        const key = `${kind}:${mid}:${tr.language}`;
        if (warmedRef.current[key]) continue;
        warmedRef.current[key] = true;
        try {
          const { data } = await axios.post(`${API}/tv/audio-mux`, { kind, id: mid, lang: tr.language }, { withCredentials: true });
          if (data.status === 'ready' && data.url) { warm(data.url); logTv('audio_mux_prewarm', { lang: tr.language }); }
        } catch { /* ignore */ }
      }
      if (selectedAudio !== '__original__') {
        const orig = activeM?.video_url || content?.video_url;
        const okey = `${kind}:${mid}:__original__`;
        if (orig && !warmedRef.current[okey]) { warmedRef.current[okey] = true; warm(orig); logTv('audio_mux_prewarm', { lang: 'original' }); }
      }
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvMode, content, currentEpisode, selectedAudio]);

  // Handle audio track switch — real sync between video and external audio
  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const audio = extAudioRef.current;

    if (selectedAudio === '__original__' || !activeMedia?.audio_tracks?.length) {
      // Use video's built-in audio (French original)
      if (tvMode && muxActiveRef.current) {
        // TV : on jouait le fichier muxé → on relance le chargeur de source d'origine
        // (hls.js/natif/mp4 avec fallbacks) en reprenant à la même position. Jamais de reload.
        muxActiveRef.current = false;
        logTv('audio_mux_back_original', { t: Math.round(video.currentTime) });
        freezeFrame();
        setAudioLoading(true);
        pendingSeekRef.current = video.currentTime;
        setSrcEpoch(e => e + 1);
        return;
      }
      video.muted = muted;
      if (audio) {
        audio.pause();
        audio.src = '';
      }
      setAudioLoading(false);
      return;
    }

    if (tvMode) {
      // TV : un seul pipeline média matériel (webOS) → impossible de jouer vidéo + audio séparés.
      // On bascule sur un fichier unique fusionné côté serveur (vidéo + piste choisie, sans ré-encodage).
      let cancelled = false;
      let pollT = null;
      setAudioLoading(true);
      const swap = async () => {
        try {
          const { data } = await axios.post(`${API}/tv/audio-mux`, {
            kind: currentEpisode ? 'episode' : 'content',
            id: currentEpisode?.id || content?.id,
            lang: selectedAudio,
          }, { withCredentials: true });
          if (cancelled) return;
          if (data.status === 'ready' && data.url) {
            const t = video.currentTime;
            freezeFrame();
            if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
            muxActiveRef.current = true;
            video.removeAttribute('crossOrigin');
            video.src = data.url;
            video.load();
            const onMeta = () => {
              try { video.currentTime = t; } catch { /* ignore */ }
              video.muted = muted;
              video.play().catch(() => {});
            };
            video.addEventListener('loadedmetadata', onMeta, { once: true });
            setAudioLoading(false);
            logTv('audio_mux_swap', { lang: selectedAudio, t: Math.round(t) });
          } else if (data.status === 'generating') {
            logTv('audio_mux_generating', { lang: selectedAudio });
            pollT = setTimeout(swap, 6000);
          } else {
            logTv('audio_mux_unavailable', { lang: selectedAudio, status: data.status });
            setAudioLoading(false);
            setSelectedAudio('__original__');
          }
        } catch (e) {
          if (!cancelled) {
            logTv('audio_mux_error', String(e).slice(0, 120));
            setAudioLoading(false);
            setSelectedAudio('__original__');
          }
        }
      };
      swap();
      return () => { cancelled = true; clearTimeout(pollT); };
    }

    // External audio track selected
    const track = activeMedia.audio_tracks.find(t => t.language === selectedAudio);
    if (!track?.url || !audio) return;

    // Ne PAS mettre la vidéo en pause pendant le changement de langue :
    // sur les TV (webOS), pause+play rapide fige le décodeur (écran noir, audio seul)
    const wasPlaying = !video.paused;

    // Mute video, load external audio
    video.muted = true;
    setAudioLoading(true);

    let started = false;
    let cancelled = false;
    let fallbackT = null;
    const fail = () => {
      // Piste illisible (pipeline TV) : retour automatique au son original plutôt que rester muet
      if (cancelled) return;
      logTv('audio_track_fail', { track: selectedAudio, readyState: audio.readyState, err: audio.error?.code });
      clearTimeout(fallbackT);
      setAudioLoading(false);
      audio.pause();
      audio.removeAttribute('src');
      try { audio.load(); } catch { /* ignore */ }
      video.muted = muted;
      setSelectedAudio('__original__');
    };
    const start = () => {
      if (started || cancelled) return;
      started = true;
      logTv('audio_track_start', { track: selectedAudio, readyState: audio.readyState });
      clearTimeout(fallbackT);
      audio.currentTime = video.currentTime;
      setAudioLoading(false);
      if (wasPlaying || !video.paused) {
        audio.currentTime = video.currentTime;
        if (video.paused) video.play().catch(() => {});
        // léger différé : laisse le décodeur vidéo se stabiliser avant d'ouvrir le pipeline audio (anti écran noir TV)
        setTimeout(() => {
          if (cancelled) return;
          audio.currentTime = video.currentTime;
          audio.play().catch(() => {
            setTimeout(() => { if (!cancelled) audio.play().catch(fail); }, 500);
          });
        }, 250);
      }
    };
    const onErr = () => fail();
    audio.addEventListener('canplay', start);
    audio.addEventListener('loadeddata', start);
    audio.addEventListener('error', onErr);
    audio.src = track.url;
    audio.preload = 'auto';
    audio.volume = muted ? 0 : volume * 0.75;
    audio.load();
    // Filet de sécurité : certains webOS n'émettent jamais canplay avec preload
    fallbackT = setTimeout(() => {
      if (started) return;
      if (audio.readyState >= 2) start();
      else fail();
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(fallbackT);
      audio.removeEventListener('canplay', start);
      audio.removeEventListener('loadeddata', start);
      audio.removeEventListener('error', onErr);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudio, activeMedia]);

  // Sync external audio with video: play/pause, seek, continuous sync
  useEffect(() => {
    const video = videoRef.current;
    const audio = extAudioRef.current;
    if (!video || !audio || selectedAudio === '__original__') return;

    const syncPlay = () => {
      if (audio.src) {
        audio.currentTime = video.currentTime;
        audio.play().catch(() => {});
      }
    };
    const syncPause = () => { if (audio.src) audio.pause(); };
    const syncSeek = () => {
      if (audio.src) {
        audio.currentTime = video.currentTime;
        // Resume audio if video is playing after seek
        if (!video.paused) {
          audio.play().catch(() => {});
        }
      }
    };
    // Continuous sync — correct drift if too large
    const syncTimeUpdate = () => {
      if (audio.src && !video.paused && Math.abs(audio.currentTime - video.currentTime) > 0.3) {
        audio.currentTime = video.currentTime;
      }
    };

    video.addEventListener('play', syncPlay);
    video.addEventListener('pause', syncPause);
    video.addEventListener('seeked', syncSeek);
    video.addEventListener('seeking', syncPause);
    video.addEventListener('timeupdate', syncTimeUpdate);

    // If playback is already running when listeners attach (resume with saved external audio), start the audio now
    if (!video.paused && audio.src && audio.paused) syncPlay();

    return () => {
      video.removeEventListener('play', syncPlay);
      video.removeEventListener('pause', syncPause);
      video.removeEventListener('seeked', syncSeek);
      video.removeEventListener('seeking', syncPause);
      video.removeEventListener('timeupdate', syncTimeUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudio, activeMedia, loading]);

  // Sync external audio volume with user volume controls (with gain reduction)
  useEffect(() => {
    const audio = extAudioRef.current;
    if (!audio || selectedAudio === '__original__') return;
    audio.volume = muted ? 0 : volume * 0.75;
  }, [volume, muted, selectedAudio]);

  // Handle subtitle track switch
  // Sous-titres stylés TV : rendu custom (taille + fond choisis dans les Réglages TV)
  const [cueText, setCueText] = useState('');
  const tvSubStyle = useRef({ size: 'medium', bg: 'box' });
  useEffect(() => {
    if (!tvMode) return;
    try {
      const s = JSON.parse(localStorage.getItem('tv_sub_style') || 'null');
      if (s && s.size && s.bg) tvSubStyle.current = s;
    } catch { /* ignore */ }
  }, [tvMode]);
  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    // Remove existing text tracks (added dynamically)
    const existingTracks = video.querySelectorAll('track[data-dynamic]');
    existingTracks.forEach(t => t.remove());

    if (selectedSub === 'Désactivés' || !activeMedia?.subtitles?.length) {
      // Disable all text tracks
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = 'hidden';
        video.textTracks[i].oncuechange = null;
      }
      setCueText('');
      return;
    }
    const sub = activeMedia.subtitles.find(s => s.language === selectedSub);
    if (sub?.url) {
      const track = document.createElement('track');
      track.kind = 'subtitles';
      track.label = sub.language;
      track.srclang = sub.language.slice(0, 2).toLowerCase();
      track.src = sub.url;
      track.default = true;
      track.dataset.dynamic = 'true';
      video.appendChild(track);
      // Activate the track (TV : piste cachée + rendu custom stylé via cuechange)
      setTimeout(() => {
        for (let i = 0; i < video.textTracks.length; i++) {
          const tt = video.textTracks[i];
          if (tt.label === sub.language) {
            tt.mode = tvMode ? 'hidden' : 'showing';
            if (tvMode) {
              tt.oncuechange = () => {
                const c = tt.activeCues;
                setCueText(c && c.length
                  ? Array.prototype.map.call(c, x => (x.text || '').replace(/<[^>]+>/g, '')).join('\n')
                  : '');
              };
            }
          } else { tt.mode = 'hidden'; tt.oncuechange = null; }
        }
      }, 100);
    }
    return () => setCueText('');
  }, [selectedSub, activeMedia]);

  // Save progress on pause, seek, unmount and page unload
  const lastPosRef = useRef({ t: 0, d: 0 });
  useEffect(() => { lastPosRef.current = { t: 0, d: 0 }; }, [id, currentEpisode]);
  useEffect(() => {
    const saveNow = (explicitTime) => {
      const v = videoRef.current;
      let ct = typeof explicitTime === 'number' ? explicitTime : (v ? v.currentTime : 0);
      if ((!ct || ct <= 0) && lastPosRef.current.t > 0) ct = lastPosRef.current.t;
      const dur = duration > 0 ? duration : lastPosRef.current.d;
      if (ct > 0 && dur > 0) {
        const payload = {
          content_id: id,
          profile_id: profileId,
          progress: Math.round((ct / dur) * 100),
          current_time: Math.round(ct),
          total_duration: Math.round(dur),
          episode_id: currentEpisodeRef.current?.id || undefined,
        };
        // Use sendBeacon for unload reliability
        safeBeacon(`${API}/watch-history`, payload);
      }
    };
    const track = (v) => { if (v && v.currentTime > 0) lastPosRef.current = { t: v.currentTime, d: v.duration || duration || 0 }; };
    const onPause = () => { track(videoRef.current); saveNow(); if (videoRef.current && !videoRef.current.ended) tel('pause', videoRef.current.currentTime); };
    const onPlayTel = () => { if (videoRef.current) tel('play', videoRef.current.currentTime); };
    const onRateTel = () => { if (videoRef.current) tel('rate', videoRef.current.currentTime, { rate: videoRef.current.playbackRate }); };
    const onEndedTel = () => { if (videoRef.current) tel('ended', videoRef.current.currentTime); };
    const onSeeked = () => { track(videoRef.current); saveNow(); };
    const onTime = () => track(videoRef.current);
    const onUnload = () => { saveNow(); telFlush(); };
    const video = videoRef.current;
    if (video) {
      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlayTel);
      video.addEventListener('ratechange', onRateTel);
      video.addEventListener('ended', onEndedTel);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('timeupdate', onTime);
    }
    window.addEventListener('beforeunload', onUnload);
    return () => {
      if (video) {
        video.removeEventListener('pause', onPause);
        video.removeEventListener('play', onPlayTel);
        video.removeEventListener('ratechange', onRateTel);
        video.removeEventListener('ended', onEndedTel);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('timeupdate', onTime);
      }
      window.removeEventListener('beforeunload', onUnload);
      saveNow(lastPosRef.current.t);
      telFlush();
    };
  }, [duration, id]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (introActiveRef.current) return; // pendant l'intro logo, le film reste en attente
    if (videoRef.current.paused) {
      if (showPauseThumbnail) {
        setFadeToBlack(true);
        setTimeout(() => {
          setShowPauseThumbnail(false);
          videoRef.current?.play().catch(() => {});
          setPlaying(true);
          setShowClassification(true);
          setShowFilterWarning(true);
          setTimeout(() => { setShowClassification(false); setShowFilterWarning(false); }, 5000);
          setTimeout(() => setFadeToBlack(false), 300);
        }, 400);
      } else {
        videoRef.current.play().catch(() => {});
        setPlaying(true);
      }
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, [showPauseThumbnail]);

  const skip = (sec) => {
    if (!videoRef.current || introActiveRef.current) return;
    const from = videoRef.current.currentTime;
    const nt = from + sec;
    tel(sec > 0 ? 'fwd' : 'back', from, { sec: Math.abs(sec) });
    if (sec < 0 && maybeReplayIntro(nt)) return;
    videoRef.current.currentTime = nt;
    maybeAdOnSeek(from, nt);
  };

  const doSkipIntro = useCallback(() => {
    const src = currentEpisode || content;
    const introEnd = src?.intro_end ? parseTimestamp(src.intro_end) : 0;
    if (videoRef.current && introEnd > 0) videoRef.current.currentTime = introEnd;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEpisode, content]);

  const doSkipRecap = useCallback(() => {
    const src = currentEpisode || content;
    const recapEnd = src?.recap_end ? parseTimestamp(src.recap_end) : 0;
    if (videoRef.current && recapEnd > 0) videoRef.current.currentTime = recapEnd;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEpisode, content]);

  // Découpe admin (trim virtuel début/fin, non destructif)
  const trimSrcObj = currentEpisode || content;
  const trimStart = Math.max(0, parseFloat(currentEpisode ? trimSrcObj?.trim_start : trimSrcObj?.video_trim_start) || 0);
  const trimEndRaw = parseFloat(currentEpisode ? trimSrcObj?.trim_end : trimSrcObj?.video_trim_end) || 0;
  const trimEnd = trimEndRaw > trimStart ? trimEndRaw : 0;
  const effDuration = Math.max(0.1, (trimEnd > 0 ? (duration > 0 ? Math.min(trimEnd, duration) : trimEnd) : duration) - trimStart);
  const effTime = Math.max(0, Math.min(currentTime - trimStart, effDuration));

  // Zones de scènes filtrées actives (saut de scène) → vignettes masquées sur la timeline
  const hiddenRanges = (() => {
    const src = currentEpisode || content;
    const cfs = src?.content_filters || content?.content_filters || [];
    return cfs.filter(cf => {
      const fType = cf.filter_id || cf.type;
      return fType && filters[fType] && (filterActionMap.current[fType] || 'skip') !== 'mute';
    }).map(cf => [parseTimestamp(cf.start), parseTimestamp(cf.end)]).filter(([s, e]) => e > s);
  })();
  const isHiddenTime = (t) => hiddenRanges.some(([s, e]) => t >= s && t < e);

  // Retour tout au début → l'intro logo Maya+ se rejoue avant le film
  const maybeReplayIntro = (target) => {
    if (!introSrc || target > trimStart + 0.75) return false;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = trimStart || 0;
    }
    setPlaying(false);
    setCurrentTime(trimStart || 0);
    introActiveRef.current = true;
    setIntroPlaying(true);
    if (introVideoRef.current) {
      try { introVideoRef.current.currentTime = 0; introVideoRef.current.play().catch(() => {}); } catch { /* ignore */ }
    }
    return true;
  };

  // Anti-saut de pub : un seek qui franchit un marqueur pub non-vu lance la pub,
  // puis la lecture reprend à la position choisie (déjà appliquée avant l'appel).
  const maybeAdOnSeek = (from, to) => {
    if (!hasAds || adState !== 'done' || introActiveRef.current) return false;
    if (from == null || to == null || to <= from) return false;
    const bp = getMidRollBreakpoints().find(b => !midRollTriggered.current.has(b) && from < b && to >= b);
    if (bp == null) return false;
    midRollTriggered.current.add(bp);
    lastMidRollCheckRef.current = to;
    if (videoRef.current) videoRef.current.pause();
    setPlaying(false);
    const durationMin = Math.round(duration / 60);
    axios.get(`${API}/ads/next`, { params: { content_duration: durationMin, position: 'mid-roll' }, withCredentials: true })
      .then(({ data }) => {
        if (data.ads?.length > 0) {
          setAdQueue(data.ads);
          setAdTotalCount(data.ads.length);
          setAdCurrentIndex(1);
          setAdState('mid-roll');
        } else {
          videoRef.current?.play().catch(() => {});
          setPlaying(true);
        }
      }).catch(() => {
        videoRef.current?.play().catch(() => {});
        setPlaying(true);
      });
    return true;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const target = trimStart + pct * effDuration;
    if (maybeReplayIntro(target)) return;
    if (videoRef.current) {
      const from = videoRef.current.currentTime;
      tel('seek', from, { to: Math.round(target) });
      videoRef.current.currentTime = target;
      setCurrentTime(target);
      maybeAdOnSeek(from, target);
    }
  };

  // ── Scrubbing web : glisser le curseur pour avancer/reculer (souris) ──
  const progressRef = useRef(null);
  const scrubbingWebRef = useRef(false);
  const seekFromClientX = (clientX) => {
    const el = progressRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return trimStart + pct * effDuration;
  };
  const startScrub = (e) => {
    if (tvMode || e.button !== 0) return;
    e.preventDefault();
    scrubbingWebRef.current = true;
    const scrubFrom = videoRef.current ? videoRef.current.currentTime : null;
    const apply = (clientX) => {
      const target = seekFromClientX(clientX);
      if (target == null) return;
      setCurrentTime(target);
      if (videoRef.current) videoRef.current.currentTime = target;
    };
    apply(e.clientX);
    const onMove = (ev) => { if (scrubbingWebRef.current) apply(ev.clientX); };
    const onUp = (ev) => {
      scrubbingWebRef.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const target = seekFromClientX(ev.clientX);
      if (target != null) tel('seek', Math.round(scrubFrom || 0), { to: Math.round(target) });
      if (target != null && maybeReplayIntro(target)) return;
      apply(ev.clientX);
      if (target != null) maybeAdOnSeek(scrubFrom, target);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Épisode suivant automatique + écran de fin ──
  const goNextEpisode = () => {
    const eps = content?.episodes || [];
    const idx = eps.findIndex(e => e.id === currentEpisode?.id);
    setNextEpCountdown(null);
    endScreenResumedRef.current = false;
    if (idx >= 0 && idx < eps.length - 1) setCurrentEpisode(eps[idx + 1]);
  };
  const cancelAutoNext = () => {
    if (currentEpisode) autoNextCancelRef.current = currentEpisode.id;
    setNextEpCountdown(null);
  };
  const triggerEndScreen = () => {
    if (endTriggeredRef.current) return;
    endTriggeredRef.current = true;
    axios.get(`${API}/contents/${id}/similar`, { withCredentials: true })
      .then(({ data }) => {
        if (data.similar) { setSimilar(data.similar); setEndScreen('offer'); setShowControls(false); }
      })
      .catch(() => {});
  };
  const handleMediaEnd = () => {
    if (!content || content.type === 'game') return;
    if (content.type === 'series' && currentEpisode) {
      const eps = content?.episodes || [];
      const idx = eps.findIndex(e => e.id === currentEpisode.id);
      if (idx >= 0 && idx < eps.length - 1) {
        if (autoNextCancelRef.current === currentEpisode.id) return; // annulé par l'utilisateur
        if (autoNextRef.current !== currentEpisode.id) { autoNextRef.current = currentEpisode.id; goNextEpisode(); }
      } else {
        if (endScreenResumedRef.current) { endScreenResumedRef.current = false; endTriggeredRef.current = false; }
        triggerEndScreen();
      }
    } else {
      // fin de film → écran de recommandation (revient même après une reprise du générique)
      if (endScreenResumedRef.current) { endScreenResumedRef.current = false; endTriggeredRef.current = false; }
      triggerEndScreen();
    }
  };

  // Clic sur le mini-lecteur : reprendre le générique en plein écran (animation inverse)
  const resumeFromEndScreen = () => {
    if (videoRef.current?.ended) return; // générique terminé, rien à reprendre
    endScreenResumedRef.current = true;
    setEndScreen(null);
    setSimilar(null);
    if (videoRef.current) {
      videoRef.current.muted = false;
      if (videoRef.current.paused) {
        videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
      }
    }
  };

  // Fin complète du média → progression 100% (retire le contenu de "Continuer la lecture")
  const markCompleted = () => {
    const d = (videoRef.current && isFinite(videoRef.current.duration) && videoRef.current.duration) || duration;
    if (!d) return;
    axios.post(`${API}/watch-history`, {
      content_id: id, profile_id: profileId, progress: 100,
      current_time: Math.round(d), total_duration: Math.round(d),
      episode_id: currentEpisodeRef.current?.id || undefined,
    }, { withCredentials: true }).catch(() => {});
  };

  // TV : la touche Retour annule l'enchaînement automatique pendant le compte à rebours
  useEffect(() => {
    if (!tvMode || nextEpCountdown == null) return;
    const onKey = (e) => {
      if ([27, 8, 10009, 461].includes(e.keyCode)) {
        e.preventDefault(); e.stopImmediatePropagation();
        cancelAutoNext();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvMode, nextEpCountdown == null]);

  const handleProgressHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverTime(pct * effDuration);
  };

  const handleVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height));
    setVolume(pct);
    if (videoRef.current) {
      videoRef.current.volume = pct;
      setMuted(pct === 0);
    }
  };

  const toggleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    if (videoRef.current) {
      // Only unmute video if using original audio
      videoRef.current.muted = selectedAudio === '__original__' ? newMuted : true;
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) { containerRef.current.requestFullscreen?.(); setFullscreen(true); }
    else { document.exitFullscreen?.(); setFullscreen(false); }
  };

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => { if (playing) setShowControls(false); }, 3500);
  }, [playing]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}` : `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const remaining = Math.max(0, effDuration - effTime);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (tvMode) return; // TV D-pad keys handled by TvPlayerOverlay
      // TV remote back button (Tizen 10009 / webOS 461)
      if (e.keyCode === 10009 || e.keyCode === 461) {
        if (showFilters) setShowFilters(false);
        else if (showAudioSub) setShowAudioSub(false);
        else if (showEpisodes) setShowEpisodes(false);
        else navigate(-1);
        return;
      }
      switch (e.key) {
        case ' ': e.preventDefault(); togglePlay(); break;
        case 'Enter':
          if (sessionStorage.getItem('tvMode') && !showFilters && !showAudioSub && !showEpisodes) { e.preventDefault(); togglePlay(); }
          break;
        case 'ArrowRight': skip(10); break;
        case 'ArrowLeft': skip(-10); break;
        case 'f': toggleFullscreen(); break;
        case 'm': toggleMute(); break;
        case 'Escape':
          if (showFilters) setShowFilters(false);
          else if (showAudioSub) setShowAudioSub(false);
          else if (showEpisodes) setShowEpisodes(false);
          break;
        default: break;
      }
      showControlsTemporarily();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [togglePlay, showControlsTemporarily, showFilters, showAudioSub, showEpisodes]);

  const closeAllPanels = () => { setShowAudioSub(false); setShowEpisodes(false); };

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-10 h-10 border-[3px] border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
      </div>
    );
  }

  if (geoBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black" data-testid="geo-blocked-page">
        <div className="text-center px-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6"><X size={40} className="text-red-400" /></div>
          <h2 className="text-2xl font-bold text-white mb-3">Contenu non disponible</h2>
          <p className="text-white/50 text-base mb-6">Ce contenu n'est pas disponible dans votre pays ({geoCountry}).</p>
          <button onClick={() => navigate(-1)} className="px-8 py-3 bg-[#E9641A] text-white rounded-lg font-semibold text-base hover:bg-[#FE975E] transition-colors">Retour</button>
        </div>
      </div>
    );
  }

  if (streamBlock) {
    const plural = (streamBlock.max_screens || 1) > 1 ? 's' : '';
    const remote = streamBlock.remote_disconnect;
    const geoB = streamBlock.geo_blocked;
    const subReq = streamBlock.subscription_required;
    return (
      <div className="min-h-screen flex items-center justify-center bg-black" data-testid="screen-limit-page">
        <div className="text-center px-8 max-w-xl">
          <div className="w-20 h-20 rounded-full bg-orange-500/15 flex items-center justify-center mx-auto mb-6"><X size={40} className="text-[#E9641A]" /></div>
          <h2 className="text-2xl font-bold text-white mb-3">{subReq ? 'Abonnement requis' : geoB ? 'Contenu non disponible' : remote ? 'Cet écran a été déconnecté' : "Trop d'écrans en cours de lecture"}</h2>
          {subReq ? (
            <p className="text-white/50 text-base mb-6" data-testid="sub-required-msg">
              Votre compte n'a pas d'abonnement actif. Abonnez-vous sur mayaplus.fr pour regarder ce contenu.
            </p>
          ) : geoB ? (
            <p className="text-white/50 text-base mb-6" data-testid="geo-blocked-msg">
              Ce contenu n'est pas disponible dans votre pays{streamBlock.country ? ` (${streamBlock.country})` : ''}.
            </p>
          ) : remote ? (
            <p className="text-white/50 text-base mb-6" data-testid="remote-disconnect-msg">La lecture a été arrêtée à distance depuis la page « Appareils » de votre compte.</p>
          ) : (
          <p className="text-white/50 text-base mb-5">
            Votre offre{streamBlock.plan_name ? ` « ${streamBlock.plan_name} »` : ''} permet de regarder sur {streamBlock.max_screens || 1} écran{plural} en simultané.
          </p>
          )}
          <div className="space-y-3 mb-6 text-left">
            {(streamBlock.sessions || []).map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl px-5 py-4" data-testid={`active-session-${i}`}>
                <p className="text-white text-sm font-semibold">{s.profile_name} regarde {s.content_title ? `« ${s.content_title} »` : 'un contenu'}</p>
                <p className="text-white/45 text-xs mt-1">{s.device} · {s.city}</p>
              </div>
            ))}
          </div>
          {!remote && !geoB && !subReq && <p className="text-white/35 text-xs mb-6">Dès qu'un écran arrête sa lecture, vous pouvez regarder ici immédiatement.</p>}
          <div className="flex items-center justify-center gap-3">
            {subReq && !tvMode && (
            <button onClick={() => navigate('/signup/plans')}
              className="px-8 py-3 bg-[#E9641A] text-white rounded-lg font-semibold text-base hover:bg-[#FE975E] transition-all"
              data-testid="sub-required-subscribe-btn">S'abonner</button>
            )}
            {!geoB && !subReq && (
            <button onClick={() => setStreamRetry(x => x + 1)}
              className={`px-8 py-3 bg-[#E9641A] text-white rounded-lg font-semibold text-base hover:bg-[#FE975E] transition-all ${blockBtn === 0 ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-105' : ''}`}
              data-testid="stream-retry-btn">Réessayer</button>
            )}
            <button onClick={() => navigate(-1)}
              className={`px-8 py-3 rounded-lg font-semibold text-base transition-all ${blockBtn === 1 ? 'bg-white text-black ring-2 ring-white ring-offset-2 ring-offset-black scale-105' : 'bg-white/10 text-white hover:bg-white/20'}`}
              data-testid="stream-back-btn">Retour</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-screen bg-black select-none"
      onMouseMove={showControlsTemporarily}
      onContextMenu={(e) => e.preventDefault()}
      style={{ cursor: showControls ? 'default' : 'none' }}
      data-testid="video-player-page"
    >
      <PlayerShield user={user} videoRef={videoRef} tvMode={tvMode} content={content} active={!endScreen} />
      {/* Video */}
      <video
        ref={videoRef}
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload noremoteplayback noplaybackrate"
        className={`absolute object-contain transition-all duration-1000 ease-in-out ${endScreen
          ? `top-[13%] left-[4%] w-[24%] h-[24%] z-[62] bg-black rounded shadow-2xl ring-1 ring-white/25 cursor-pointer hover:ring-2 hover:ring-white/60 ${(endScreen !== 'offer' || creditsEnded) ? 'opacity-0 invisible pointer-events-none' : ''}`
          : 'top-0 left-0 w-full h-full'}`}
        title={endScreen ? 'Continuer à regarder le générique en plein écran' : undefined}
        data-testid={endScreen ? 'endscreen-mini-player' : 'main-video'}
        onClick={endScreen ? resumeFromEndScreen : togglePlay}
        onCanPlay={() => {
          // Don't auto-play if ad is showing
          if (adState === 'pre-roll' || adState === 'mid-roll') {
            if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
            return;
          }
          // L'intro logo joue par-dessus : le film reste préchargé en pause à 0
          if (introActiveRef.current) {
            if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
            return;
          }
          if (videoRef.current && videoRef.current.paused && !playing) {
            videoRef.current.play().then(() => setPlaying(true)).catch(() => {
              if (videoRef.current) {
                videoRef.current.muted = true;
                setMuted(true);
                videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
              }
            });
          }
        }}
        onTimeUpdate={() => {
          if (scrubbingWebRef.current) return;
          if (!videoRef.current) return;
          const t = videoRef.current.currentTime;
          setCurrentTime(t);
          // Fenêtre de découpe admin
          if (trimStart > 0 && t < trimStart - 0.4) { videoRef.current.currentTime = trimStart; return; }
          if (trimEnd > 0 && t >= trimEnd) { videoRef.current.pause(); setPlaying(false); markCompleted(); handleMediaEnd(); return; }
          // Source for intro/outro/filters: episode takes priority over content
          const src = currentEpisode || content;
          // Skip intro detection — intro_start defaults to 0 if empty but intro_end is set
          const introEnd = src?.intro_end ? parseTimestamp(src.intro_end) : -1;
          const introStart = introEnd > 0 ? (src?.intro_start ? parseTimestamp(src.intro_start) : 0) : -1;
          if (introEnd > 0 && introStart >= 0) {
            setShowSkipIntro(t >= introStart && t < introEnd);
          } else { setShowSkipIntro(false); }
          // Skip recap detection — same pattern as intro
          const recapEnd = src?.recap_end ? parseTimestamp(src.recap_end) : -1;
          const recapStart = recapEnd > 0 ? (src?.recap_start ? parseTimestamp(src.recap_start) : 0) : -1;
          if (recapEnd > 0 && recapStart >= 0) {
            setShowSkipRecap(t >= recapStart && t < recapEnd);
          } else { setShowSkipRecap(false); }
          // Outro / Next episode detection
          const outroStart = src?.outro_start ? parseTimestamp(src.outro_start) : -1;
          // Outro fin non renseignée → fin du média automatiquement
          const outroEnd = src?.outro_end ? parseTimestamp(src.outro_end) : (outroStart > 0 ? (videoRef.current.duration || 0) : -1);
          const dur = videoRef.current.duration || 0;
          const hasNextEp = content?.episodes?.length > 0 && currentEpisode && (() => {
            const eps = content.episodes;
            const idx = eps.findIndex(e => e.id === currentEpisode.id);
            return idx >= 0 && idx < eps.length - 1;
          })();
          if (outroStart > 0 && outroEnd > 0) {
            // Show next ep during outro window
            setShowNextEp(hasNextEp && t >= outroStart && t <= outroEnd);
          } else if (hasNextEp && dur > 0) {
            // No outro defined: show next ep in the last 30 seconds
            setShowNextEp(t >= dur - 30);
          } else {
            setShowNextEp(false);
          }
          // Épisode suivant automatique (avec compte à rebours annulable) / écran de fin
          if (content?.type === 'series' && currentEpisode) {
            if (hasNextEp) {
              const boundary = (outroStart > 0 && outroEnd > 0) ? Math.min(outroEnd, dur || outroEnd) : (dur > 0 ? dur : -1);
              if (boundary > 0 && autoNextCancelRef.current !== currentEpisode.id) {
                if (t >= boundary - 0.3) {
                  if (autoNextRef.current !== currentEpisode.id) { autoNextRef.current = currentEpisode.id; goNextEpisode(); }
                  return;
                }
                if (t >= boundary - 5.3) {
                  const rem = Math.max(1, Math.ceil(boundary - t - 0.3));
                  setNextEpCountdown(prev => (prev === rem ? prev : rem));
                } else {
                  setNextEpCountdown(prev => (prev === null ? prev : null));
                }
              } else {
                setNextEpCountdown(prev => (prev === null ? prev : null));
              }
            } else if (!endScreen && !endScreenResumedRef.current && outroStart > 0 && t >= outroStart) {
              triggerEndScreen();
            }
          } else if (content && content.type !== 'series' && content.type !== 'game' && !endScreen && !endScreenResumedRef.current) {
            // Fin de film : à "Outro début", sinon par défaut les 10 dernières secondes
            const effEnd = trimEnd > 0 ? Math.min(trimEnd, dur || trimEnd) : dur;
            const movieOutro = outroStart > 0 ? outroStart : (effEnd > 15 ? effEnd - 10 : -1);
            if (movieOutro > 0 && t >= movieOutro) triggerEndScreen();
          }
          // Content filters - dynamically skip scene or mute audio based on admin-defined filter actions
          const cFilters = src?.content_filters || content?.content_filters || [];
          for (const cf of cFilters) {
            const fs = parseTimestamp(cf.start);
            const fe = parseTimestamp(cf.end);
            const fType = cf.filter_id || cf.type;
            if (!fType) continue;
            const isActive = filters[fType];
            const announceKey = `${fType}_${fs}`;
            // Reset announcement flag when user seeks before the filter zone
            if (t < (fs - 2) && filterAnnouncementRef.current[announceKey]) {
              filterAnnouncementRef.current[announceKey] = false;
            }
            // Show announcement 2s before filter triggers
            if (isActive && t >= (fs - 2) && t < fs && !filterAnnouncementRef.current[announceKey]) {
              filterAnnouncementRef.current[announceKey] = true;
              const filterDef = adminFilters.find(f => f.id === fType);
              if (filterDef) {
                setFilterAnnouncement(filterDef.name);
                setTimeout(() => setFilterAnnouncement(null), 3000);
              }
            }
            if (t >= fs && t < fe && isActive) {
              const action = filterActionMap.current[fType] || 'skip';
              if (action === 'mute') {
                videoRef.current.muted = true;
                if (extAudioRef.current && extAudioRef.current.src) extAudioRef.current.volume = 0;
                setTimeout(() => {
                  if (videoRef.current && videoRef.current.currentTime >= fe) {
                    videoRef.current.muted = selectedAudio !== '__original__' ? true : muted;
                    if (extAudioRef.current && extAudioRef.current.src) extAudioRef.current.volume = muted ? 0 : volume * 0.75;
                  }
                }, 200);
              } else {
                videoRef.current.currentTime = fe; // skip
              }
            }
          }
        }}
        onLoadedMetadata={() => {
          if (!videoRef.current) return;
          setDuration(videoRef.current.duration);
          setAudioLoading(false);
          // Reprise après « Réessayer » (position sauvegardée avant rechargement)
          let retryAt = null;
          try { retryAt = sessionStorage.getItem('mayaRetryAt'); if (retryAt != null) sessionStorage.removeItem('mayaRetryAt'); } catch {}
          if (retryAt != null && parseFloat(retryAt) > 0) {
            pendingSeekRef.current = null;
            videoRef.current.currentTime = Math.max(parseFloat(retryAt), trimStart);
            return;
          }
          // Apply deferred resume seek (episode restored from watch history)
          if (pendingSeekRef.current != null) {
            const at = Math.max(pendingSeekRef.current, trimStart);
            pendingSeekRef.current = null;
            videoRef.current.currentTime = at;
            showResumeToast(at);
          } else if (trimStart > 0 && videoRef.current.currentTime < trimStart) {
            videoRef.current.currentTime = trimStart;
          }
        }}
        onEnded={() => { setPlaying(false); markCompleted(); setCreditsEnded(true); handleMediaEnd(); }}
        onError={(e) => {
          const video = e.target;
          const err = video?.error;
          if (err && err.code) {
            // En HLS (hls.js), les erreurs de décodage/réseau sont auto-récupérées par le handler Hls.Events.ERROR
            // (recoverMediaError / fallback). Ne pas afficher l'écran d'erreur prématurément.
            if (hlsRef.current && (err.code === 3 || err.code === 2)) {
              console.warn('Video error laissé à la récupération hls.js:', err.code, err.message);
              return;
            }
            const messages = { 1: 'Lecture interrompue', 2: 'Erreur réseau', 3: 'Erreur de décodage', 4: 'Format non supporté' };
            setVideoError(messages[err.code] || `Erreur vidéo (code ${err.code})`);
            console.error('Video error:', err.code, err.message);
            if (tvMode) logTv('video_error', { id: content?.id, code: err.code, msg: String(err.message || '').slice(0, 120),
              src: String(video.currentSrc || video.src || '').slice(0, 160), net: video.networkState, ready: video.readyState });
          }
        }}
        onPlay={() => {
          setPlaying(true);
          setCreditsEnded(false);
          clearTimeout(pauseThumbnailTimer.current);
          setShowPauseThumbnail(false);
        }}
        onPause={() => {
          setPlaying(false);
          // Show thumbnail after 3s of pause (web only — TV has its own pause overlay)
          if (!tvMode) {
            clearTimeout(pauseThumbnailTimer.current);
            pauseThumbnailTimer.current = setTimeout(() => setShowPauseThumbnail(true), 3000);
          }
        }}
      />

      {/* Hidden audio element for external audio tracks */}
      <audio ref={extAudioRef} style={{ display: 'none' }} />

      {/* Image gelée pendant la bascule de piste audio (aucun écran noir) */}
      <canvas ref={freezeCanvasRef} data-testid="audio-freeze-frame"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain',
          background: '#000', zIndex: 5, display: freezeOn && !freezeSprite ? 'block' : 'none', pointerEvents: 'none' }} />
      {freezeOn && freezeSprite && (
        <div data-testid="audio-freeze-sprite" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 5, pointerEvents: 'none',
          backgroundColor: '#000', backgroundImage: `url(${freezeSprite.url})`, backgroundSize: freezeSprite.size,
          backgroundPosition: freezeSprite.pos, backgroundRepeat: 'no-repeat',
          filter: freezeSprite.blur ? 'blur(0.3vw)' : 'none' }} />
      )}

      {/* Sous-titres stylés TV (taille + fond configurés dans les Réglages TV) */}
      {tvMode && cueText && !streamBlock && (
        <div data-testid="tv-subtitle-overlay" style={{ position: 'absolute', left: '8%', right: '8%', bottom: '7vh', zIndex: 30, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
          <span style={{
            fontSize: ({ small: '1.9vw', medium: '2.6vw', large: '3.5vw' })[tvSubStyle.current.size] || '2.6vw',
            fontWeight: 600, textAlign: 'center', lineHeight: 1.35, whiteSpace: 'pre-line', color: '#fff',
            ...(({
              none: { textShadow: '0 0 0.35vw rgba(0,0,0,0.9)' },
              shadow: { textShadow: '0.12vw 0.12vw 0.25vw #000, -0.12vw 0.12vw 0.25vw #000, 0 0 0.7vw #000' },
              box: { background: 'rgba(0,0,0,0.72)', padding: '0.35vw 1vw', borderRadius: '0.35vw' },
            })[tvSubStyle.current.bg] || {}),
          }}>{cueText}</span>
        </div>
      )}

      {/* ── Intro logo Maya+ (bumper par-dessus le film préchargé, APRÈS la pub pré-roll) ── */}
      {introPlaying && introSrc && !streamBlock && adState === 'done' && (
        <div className="absolute inset-0 z-[70] bg-black" data-testid="intro-bumper-overlay">
          <video
            ref={introVideoRef}
            src={introSrc}
            autoPlay
            playsInline
            preload="auto"
            disablePictureInPicture
            controlsList="nodownload"
            className="w-full h-full object-contain"
            onEnded={endIntro}
            onError={endIntro}
            onCanPlay={(e) => {
              const v = e.target;
              if (v.paused) v.play().catch(() => { v.muted = true; v.play().catch(endIntro); });
            }}
            data-testid="intro-bumper-video"
          />
        </div>
      )}

      {/* ── Ad overlay ── */}
      {currentAd && (adState === 'pre-roll' || adState === 'mid-roll') && (
        <div className="absolute inset-0 z-50 bg-black flex flex-col" data-testid="ad-overlay">
          <video
            ref={adVideoRef}
            src={currentAd.video_url}
            autoPlay
            disablePictureInPicture
            controlsList="nodownload"
            className="w-full flex-1 object-contain"
            onEnded={onAdEnded}
            onError={onAdEnded}
          />

          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4">
            {/* Back button */}
            <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white transition-colors" data-testid="ad-back-btn">
              <ArrowLeft size={45} />
            </button>
            {/* Flag + timer with counter */}
            <div className="flex items-center gap-2">
              <Flag size={36} className="text-white/60" />
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-10 bg-[#E9641A] rounded-full" />
                <span className="text-white/80 text-base font-mono">Pub {Math.min(adCurrentIndex, adTotalCount) || 1} sur {adTotalCount} · {adTimeLeft}</span>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4">
            {/* Left: play/pause + volume with slider */}
            <div className="flex items-center gap-4">
              <button onClick={() => {
                if (adVideoRef.current) {
                  if (adVideoRef.current.paused) { adVideoRef.current.play().catch(() => {}); setAdPlaying(true); }
                  else { adVideoRef.current.pause(); setAdPlaying(false); }
                }
              }} className="text-white hover:text-white/80 transition-colors" data-testid="ad-play-btn">
                {adPlaying ? <Pause size={48} fill="white" /> : <Play size={48} fill="white" />}
              </button>
              <div className="relative"
                onMouseEnter={() => { clearTimeout(adVolumeTimer.current); adVolumeTimer.current = setTimeout(() => setShowAdVolume(true), 200); }}
                onMouseLeave={() => { clearTimeout(adVolumeTimer.current); setShowAdVolume(false); }}>
                <button onClick={() => {
                  if (adVideoRef.current) {
                    const newMuted = !adMuted;
                    adVideoRef.current.muted = newMuted;
                    setAdMuted(newMuted);
                  }
                }} className="text-white hover:text-white/80 transition-colors" data-testid="ad-volume-btn">
                  {adMuted || adVolume === 0 ? <VolumeX size={45} /> : <Volume2 size={45} />}
                </button>
                {showAdVolume && (
                  <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-[#2a2a2a] rounded-lg p-2 w-9 animate-scaleIn" onClick={e => e.stopPropagation()}>
                    <div className="relative w-1.5 h-28 bg-white/15 rounded-full mx-auto cursor-pointer" onClick={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = 1 - Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
                      setAdVolume(pct);
                      setAdMuted(pct === 0);
                      if (adVideoRef.current) { adVideoRef.current.volume = pct; adVideoRef.current.muted = pct === 0; }
                    }}>
                      <div className="absolute bottom-0 left-0 right-0 bg-[#E9641A] rounded-full" style={{ height: `${(adMuted ? 0 : adVolume) * 100}%` }} />
                      <div className="absolute left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-[#E9641A] rounded-full" style={{ bottom: `calc(${(adMuted ? 0 : adVolume) * 100}% - 7px)` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Right: fullscreen */}
            <button onClick={() => {
              if (containerRef.current) {
                if (document.fullscreenElement) document.exitFullscreen();
                else containerRef.current.requestFullscreen().catch(() => {});
              }
            }} className="text-white hover:text-white/80 transition-colors" data-testid="ad-fullscreen-btn">
              <Maximize size={45} />
            </button>
          </div>

          {/* Click-through link */}
          {currentAd.click_url && (
            <button onClick={onAdClick} className="absolute bottom-16 right-6 px-3 py-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold rounded-lg transition-colors" data-testid="ad-click-btn">
              En savoir plus
            </button>
          )}
        </div>
      )}


      {/* Audio track loading indicator — affichage retiré à la demande (bascule quasi instantanée) */}

      {/* Classification overlay — Netflix style */}
      {content && (content.age_rating || content.tags?.length > 0) && (
        <div
          className="absolute top-0 left-0 z-30 px-8 pt-20 pb-6 transition-opacity duration-1000"
          style={{ opacity: showClassification ? 1 : 0, pointerEvents: 'none' }}
          data-testid="classification-overlay"
        >
          <div className="flex items-stretch gap-0">
            <div className="w-1.5 bg-[#E9641A] rounded-l-sm flex-none" />
            <div className="pl-5">
              {content.age_rating && (
                <p className="text-white font-bold text-2xl tracking-wide mb-1">
                  CLASSÉ {content.age_rating}
                </p>
              )}
              {content.tags?.length > 0 && (
                <p className="text-white/70 text-lg">{content.tags.join(', ')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter content warning — shown at start and on resume */}
      {content?.content_filters?.length > 0 && adminFilters.length > 0 && (
        <div
          className="absolute top-6 right-16 sm:top-8 sm:right-24 z-40 pointer-events-none transition-opacity duration-1000 max-w-lg"
          style={{ opacity: showFilterWarning ? 1 : 0 }}
          data-testid="filter-content-warning"
        >
          <div className={tvMode ? 'bg-black/50 rounded-xl px-7 py-5' : 'bg-black/40 backdrop-blur-sm rounded-lg px-5 py-3'}>
            <p className={`text-white/80 font-medium mb-1.5 ${tvMode ? 'text-2xl' : 'text-sm'}`}>
              {content.type === 'series' ? 'Cette série' : 'Ce film'} contient des scènes de :
            </p>
            <p className={`text-white/60 ${tvMode ? 'text-xl' : 'text-sm'}`}>
              {(() => {
                const uniqueIds = [...new Set(content.content_filters.map(f => f.filter_id || f.type))];
                return uniqueIds.map(fId => {
                  const def = adminFilters.find(af => af.id === fId);
                  return def?.name;
                }).filter(Boolean).join(', ');
              })()}
            </p>
          </div>
        </div>
      )}

      {/* Filter announcement overlay — appears 2s before a filter triggers */}
      {filterAnnouncement && (
        <div
          className={`absolute z-40 pointer-events-none ${tvMode ? 'top-16 left-0 right-0 flex justify-center' : 'top-16 left-16 sm:top-20 sm:left-24'}`}
          data-testid="filter-announcement"
        >
          <div className="animate-fadeInDown">
            <div className="flex items-stretch gap-0 bg-black/60 backdrop-blur-md rounded-lg overflow-hidden">
              <div className={`${tvMode ? 'w-3' : 'w-2'} bg-[#E9641A] flex-none`} />
              <div className={tvMode ? 'px-7 py-4' : 'px-5 py-3'}>
                <p className={`text-white font-semibold ${tvMode ? 'text-2xl' : 'text-sm sm:text-base'}`}>{filterAnnouncement}</p>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Pause thumbnail overlay — fades in after 3s of pause with scale up */}
      {!tvMode && content && (
        <div
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            opacity: showPauseThumbnail && !endScreen ? 1 : 0,
            transform: showPauseThumbnail && !endScreen ? 'scale(1)' : 'scale(1.05)',
            transition: 'opacity 3s ease-in-out, transform 3s ease-out',
          }}
          data-testid="pause-thumbnail-overlay"
        >
          <img
            src={content.banner_url || content.thumbnail_url}
            alt={content.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      )}

      {/* Fade to black overlay for resume transition */}
      <div
        className="absolute inset-0 z-25 bg-black pointer-events-none transition-opacity duration-700"
        style={{ opacity: fadeToBlack ? 1 : 0 }}
      />

      {/* Resume toast */}
      {resumeToast && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-40 px-5 py-2.5 bg-black/80 backdrop-blur-md border border-white/15 rounded-lg text-white text-sm font-medium animate-fadeInDown" data-testid="resume-toast">
          {resumeToast}
        </div>
      )}

      {/* Skip intro button */}
      {showSkipIntro && !geoBlocked && (
        <button onClick={(e) => { e.stopPropagation(); doSkipIntro(); }} data-testid="skip-intro-btn"
          className={`absolute bottom-24 right-8 z-[45] backdrop-blur-md border font-semibold transition-all cursor-pointer ${tvMode ? 'px-10 py-5 text-2xl rounded-lg bg-white text-black border-white' : 'px-5 py-2.5 text-sm rounded bg-white/10 hover:bg-white/20 border-white/30 text-white'}`}>
          Passer l'intro
        </button>
      )}

      {/* Skip recap button */}
      {showSkipRecap && !geoBlocked && (
        <button onClick={(e) => { e.stopPropagation(); doSkipRecap(); }} data-testid="skip-recap-btn"
          className={`absolute ${showSkipIntro ? (tvMode ? 'bottom-52' : 'bottom-40') : 'bottom-24'} right-8 z-[45] backdrop-blur-md border font-semibold transition-all cursor-pointer ${tvMode ? 'px-10 py-5 text-2xl rounded-lg bg-white text-black border-white' : 'px-5 py-2.5 text-sm rounded bg-white/10 hover:bg-white/20 border-white/30 text-white'}`}>
          Passer le récap
        </button>
      )}

      {/* Episode next overlay — outro + compte à rebours d'enchaînement automatique */}
      {(showNextEp || nextEpCountdown !== null) && content?.episodes?.length > 0 && currentEpisode && !geoBlocked && !endScreen && (() => {
        const eps = content.episodes || [];
        const currentEpIdx = eps.findIndex(e => e.id === currentEpisode?.id);
        const hasNext = currentEpIdx >= 0 && currentEpIdx < eps.length - 1;
        if (!hasNext) return null;
        return (
          <div className="absolute bottom-24 right-8 z-[45] flex flex-col items-end gap-1.5">
            {tvMode && nextEpCountdown !== null && (
              <span className="text-white/60 text-lg font-medium drop-shadow" data-testid="autonext-tv-hint">OK : lancer maintenant · Retour : annuler</span>
            )}
            <div className="flex items-center gap-3">
              {nextEpCountdown !== null && (
                <button onClick={(e) => { e.stopPropagation(); cancelAutoNext(); }} data-testid="cancel-autonext-btn"
                  className={`backdrop-blur-md bg-black/40 hover:bg-black/60 border border-white/30 text-white font-semibold transition-all cursor-pointer ${tvMode ? 'px-10 py-5 text-2xl rounded-lg' : 'px-5 py-2.5 text-sm rounded'}`}>
                  Annuler
                </button>
              )}
              <button onClick={(e) => { e.stopPropagation(); setCurrentEpisode(eps[currentEpIdx + 1]); setNextEpCountdown(null); }} data-testid="next-episode-btn"
                className={`backdrop-blur-md border font-semibold transition-all cursor-pointer ${tvMode ? 'px-10 py-5 text-2xl rounded-lg bg-white text-black border-white' : 'px-5 py-2.5 text-sm rounded bg-white/10 hover:bg-white/20 border-white/30 text-white'}`}>
                {nextEpCountdown !== null ? `Épisode suivant dans ${nextEpCountdown}s` : 'Épisode suivant'}
              </button>
            </div>
          </div>
        );
      })()}


      {/* Controls overlay */}
      {!tvMode && (
      <div className={`absolute inset-0 z-30 flex flex-col justify-between transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>

        {/* Top bar */}
        <div className="flex items-center justify-between px-8 pt-6 bg-gradient-to-b from-black/60 to-transparent pb-20" onClick={e => e.stopPropagation()}>
          <PlayerBtn testId="player-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={45} />
          </PlayerBtn>
          <PlayerBtn testId="player-report-btn" onClick={openReport}>
            <Flag size={42} />
          </PlayerBtn>
        </div>

        {/* Center zone — click to toggle play/pause */}
        <div className="flex-1 flex items-center justify-center cursor-pointer" onClick={togglePlay}>
          {videoError ? (
            <div className="flex flex-col items-center gap-4 text-center" onClick={e => e.stopPropagation()}>
              <div className="text-red-400 text-base font-semibold">{videoError}</div>
              <button data-testid="player-retry-btn"
                className="px-6 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold text-sm transition-colors"
                onClick={() => {
                  setVideoError(null);
                  // Vrai retry : mémorise la position (léger recul) puis réinitialise tout le pipeline
                  // (HLS signé compris) via un rechargement — évite l'ancien fallback .mov brut qui replantait Firefox.
                  try {
                    const t = videoRef.current?.currentTime || 0;
                    if (t > 5) sessionStorage.setItem('mayaRetryAt', String(Math.max(0, t - 2)));
                  } catch {}
                  window.location.reload();
                }}>
                Réessayer
              </button>
            </div>
          ) : !playing && (
            <button data-testid="player-center-play"
              className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center hover:bg-white/25 transition-all hover:scale-110"
              onClick={e => e.stopPropagation()}>
              <Play size={36} fill="white" className="text-white ml-1" onClick={togglePlay} />
            </button>
          )}
        </div>

        {/* Bottom controls */}
        <div className="bg-gradient-to-t from-black/70 to-transparent pt-20 px-8 pb-5" onClick={e => e.stopPropagation()}>
          {/* Progress bar */}
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm text-white/80 w-16 text-left" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }} data-testid="player-current-time">{formatTime(effTime)}</span>
            <div ref={progressRef} className="flex-1 relative h-1.5 bg-white/20 rounded-full cursor-pointer group" onMouseDown={startScrub} onClick={handleSeek} onMouseMove={handleProgressHover} onMouseLeave={() => setHoverTime(null)} data-testid="player-progress">
              <div className="absolute inset-y-0 left-0 bg-[#E9641A] rounded-full" style={{ width: `${(effTime / effDuration) * 100}%` }} />
              {hasAds && getMidRollBreakpoints().map(bp => {
                const pct = ((bp - trimStart) / effDuration) * 100;
                if (pct <= 0 || pct >= 100) return null;
                return <div key={bp} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#3B82F6] ring-1 ring-black/40 z-10" style={{ left: `${pct}%` }} data-testid="ad-marker" title="Publicité" />;
              })}
              {hoverTime !== null && (() => {
                const spriteSrc = currentEpisode?.sprite_url ? currentEpisode : (content?.sprite_url ? content : null);
                const hidden = isHiddenTime(trimStart + hoverTime);
                return (
                <div className="absolute bottom-full mb-2 -translate-x-1/2 flex flex-col items-center pointer-events-none" style={{ left: `clamp(88px, ${(hoverTime / effDuration) * 100}%, calc(100% - 88px))` }} data-testid="progress-hover-tooltip">
                  {hidden ? (
                    <div className="rounded-md overflow-hidden bg-[#0d0d14] border border-white/25 shadow-2xl mb-1.5 flex flex-col items-center justify-center gap-1" style={{ width: 176, height: 99 }} data-testid="progress-preview-hidden">
                      <EyeOff size={20} className="text-white/50" />
                      <span className="text-[10px] text-white/50 font-semibold">Scène filtrée</span>
                    </div>
                  ) : (
                  <ProgressPreview src={currentEpisode?.video_url || content?.video_url || ''} time={trimStart + hoverTime}
                    sprite={spriteSrc?.sprite_url} spriteMeta={spriteSrc?.sprite_meta} />
                  )}
                  <div className="px-2 py-1 bg-black/90 rounded text-xs text-white whitespace-nowrap" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }}>
                    {formatTime(hoverTime)}
                  </div>
                </div>
                );
              })()}
              <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#E9641A] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                style={{ left: `calc(${(effTime / effDuration) * 100}% - 8px)` }} />
            </div>
            <span className="text-sm text-white/60 w-16 text-right" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }}>{formatTime(remaining)}</span>
          </div>

          {/* Button row */}
          <div className="flex items-center justify-between">
            {/* Left controls */}
            <div className="flex items-center gap-6">
              <PlayerBtn testId="player-play-btn" onClick={togglePlay}>
                {playing ? <Pause size={48} /> : <Play size={48} fill="white" />}
              </PlayerBtn>

              <PlayerBtn testId="player-rewind-btn" onClick={() => skip(-10)}>
                <Rewind10Icon />
              </PlayerBtn>

              <PlayerBtn testId="player-forward-btn" onClick={() => skip(10)}>
                <Forward10Icon />
              </PlayerBtn>

              {/* Volume with vertical slider */}
              <div className="relative"
                onMouseEnter={() => { clearTimeout(volumeTimer.current); volumeTimer.current = setTimeout(() => setShowVolume(true), 200); }}
                onMouseLeave={() => { clearTimeout(volumeTimer.current); setShowVolume(false); }}>
                <PlayerBtn testId="player-mute-btn" onClick={toggleMute}>
                  <VolumeIcon size={45} />
                </PlayerBtn>
                {showVolume && (
                  <div className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#2a2a2a] rounded-lg p-2 w-9 animate-scaleIn" data-testid="volume-slider" onClick={e => e.stopPropagation()}>
                    <div className="relative w-1.5 h-28 bg-white/15 rounded-full mx-auto cursor-pointer" onClick={handleVolumeChange} data-testid="volume-track">
                      <div className="absolute bottom-0 left-0 right-0 bg-[#E9641A] rounded-full" style={{ height: `${(muted ? 0 : volume) * 100}%` }} />
                      <div className="absolute left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-[#E9641A] rounded-full"
                        style={{ bottom: `calc(${(muted ? 0 : volume) * 100}% - 7px)` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Center title */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-5 text-center pointer-events-none">
              <span className="text-base text-white/80 font-semibold">{content?.title}</span>
              {content?.type === 'series' && currentEpisode && (
                <span className="text-base text-white/40 ml-2">
                  S{currentEpisode.season || 1}E{currentEpisode.episode || '?'} {currentEpisode.title || ''}
                </span>
              )}
            </div>

            {/* Right controls */}
            <div className="flex items-center gap-6">
              {/* Previous episode */}
              {content?.type === 'series' && (() => {
                const eps = content.episodes || [];
                const idx = eps.findIndex(e => e.id === currentEpisode?.id);
                return idx > 0 ? (
                  <PlayerBtn testId="player-prev-ep-btn" onClick={() => setCurrentEpisode(eps[idx - 1])}>
                    <PrevEpisodeIcon />
                  </PlayerBtn>
                ) : null;
              })()}
              {/* Next episode */}
              {content?.type === 'series' && (() => {
                const eps = content.episodes || [];
                const idx = eps.findIndex(e => e.id === currentEpisode?.id);
                return idx >= 0 && idx < eps.length - 1 ? (
                  <PlayerBtn testId="player-next-ep-btn" onClick={() => setCurrentEpisode(eps[idx + 1])}>
                    <NextEpisodeIcon />
                  </PlayerBtn>
                ) : null;
              })()}

              {/* Episodes list */}
              {content?.type === 'series' && (
                <PlayerBtn testId="player-episodes-btn" onClick={() => { closeAllPanels(); setShowEpisodes(!showEpisodes); }}>
                  <EpisodesListIcon />
                </PlayerBtn>
              )}

              {/* Audio/Subtitles — hover to open */}
              <div className="relative"
                onMouseEnter={() => { setShowFilters(false); setShowEpisodes(false); setShowAudioSub(true); }}
                onMouseLeave={() => setShowAudioSub(false)}>
                <PlayerBtn testId="player-audiosub-btn" onClick={() => {}}>
                  <SubtitlesIcon />
                </PlayerBtn>
                {showAudioSub && (
                  <AudioSubPanel onClose={() => setShowAudioSub(false)} selectedAudio={selectedAudio} setSelectedAudio={setSelectedAudio} selectedSub={selectedSub} setSelectedSub={setSelectedSub} content={activeMedia} />
                )}
              </div>

              {/* Filters — hover to open */}
              <div className="relative"
                onMouseEnter={() => { setShowAudioSub(false); setShowEpisodes(false); setShowFilters(true); }}
                onMouseLeave={() => setShowFilters(false)}>
                <PlayerBtn testId="player-filters-btn" onClick={() => {}}>
                  <FiltersIcon />
                </PlayerBtn>
                {showFilters && (
                  <FiltersPanel filters={filters} setFilters={setFilters} onClose={() => setShowFilters(false)} adminFilters={adminFilters} />
                )}
              </div>

              {/* Fullscreen */}
              <PlayerBtn testId="player-fullscreen-btn" onClick={toggleFullscreen}>
                {fullscreen ? <Minimize size={45} /> : <Maximize size={45} />}
              </PlayerBtn>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* TV D-pad player overlay (pause UI, scrubbing, pause screensaver) */}
      {tvMode && !endScreen && (() => {
        const eps = content?.episodes || [];
        const epIdx = eps.findIndex(ep => ep.id === currentEpisode?.id);
        return (
          <TvPlayerOverlay
            content={activeMedia}
            episode={currentEpisode}
            playing={playing}
            currentTime={currentTime}
            duration={duration}
            adActive={(!!currentAd && (adState === 'pre-roll' || adState === 'mid-roll')) || introPlaying}
            videoError={videoError}
            videoRef={videoRef}
            mp4Url={currentEpisode?.video_url || content?.video_url || ''}
            onTogglePlay={togglePlay}
            onSeek={skip}
            onSeekCommit={(f, t) => { tel('seek', Math.round(f || 0), { to: Math.round(t || 0) }); maybeAdOnSeek(f, t); }}
            hiddenRanges={hiddenRanges}
            onRestart={() => { if (!videoRef.current) return; videoRef.current.currentTime = trimStart || 0; if (maybeReplayIntro(trimStart || 0)) return; videoRef.current.play().catch(() => {}); setPlaying(true); }}
            hasPrevEp={content?.type === 'series' && epIdx > 0}
            hasNextEp={content?.type === 'series' && epIdx >= 0 && epIdx < eps.length - 1}
            onPrevEp={() => { if (epIdx > 0) setCurrentEpisode(eps[epIdx - 1]); }}
            onNextEp={() => { if (epIdx >= 0 && epIdx < eps.length - 1) setCurrentEpisode(eps[epIdx + 1]); }}
            onBack={() => { if (tvMode) tvExit(); else navigate(-1); }}
            showSkipIntro={showSkipIntro && !geoBlocked}
            showSkipRecap={showSkipRecap && !geoBlocked}
            showNextEpBtn={showNextEp && content?.type === 'series'}
            onSkipIntro={doSkipIntro}
            onSkipRecap={doSkipRecap}
            onAdToggle={() => { const v = adVideoRef.current; if (!v) return; if (v.paused) { v.play().catch(() => {}); setAdPlaying(true); } else { v.pause(); setAdPlaying(false); } }}
            selectedAudio={selectedAudio} setSelectedAudio={setSelectedAudio}
            selectedSub={selectedSub} setSelectedSub={setSelectedSub}
            filters={filters} setFilters={setFilters} adminFilters={adminFilters}
            pauseAd={pauseAd} formatTime={formatTime}
            adMarkers={hasAds ? getMidRollBreakpoints() : []}
            onReport={openReport} reportOpen={showReport}
          />
        );
      })()}

      {/* Panels */}
      {showEpisodes && content && (
        <EpisodesPanel content={content} currentEpisodeId={currentEpisode?.id} onSelectEpisode={(ep) => { setCurrentEpisode(ep); setShowEpisodes(false); }} onClose={() => setShowEpisodes(false)} />
      )}

      {/* ── Écran de fin de série : recommandation d'un contenu similaire ── */}
      {endScreen && similar && (
        <EndScreen
          rec={similar}
          phase={endScreen}
          setPhase={setEndScreen}
          tvMode={tvMode}
          creditsEnded={creditsEnded}
          onResume={resumeFromEndScreen}
          onTrailerStart={() => { if (videoRef.current) { videoRef.current.muted = true; } }}
          onWatch={() => {
            if (tvMode) tvNavigate(`/watch/${similar.id}`, { id: similar.id, title: similar.title });
            else navigate(`/watch/${similar.id}`);
          }}
          onMoreInfo={() => {
            if (tvMode) tvNavigate('/tv', { id: similar.id, title: similar.title });
            else setMoreInfoId(similar.id);
          }}
          onExit={() => {
            if (tvMode) tvExit();
            else navigate('/');
          }}
        />
      )}
      {moreInfoId && !tvMode && (
        <ContentModal contentId={moreInfoId} onClose={() => setMoreInfoId(null)} />
      )}

      {/* Signalement de problème */}
      {showReport && (
        <ReportProblemModal
          content={content} episode={currentEpisode} timecode={reportTcRef.current}
          profile={activeProfile} platform={tvMode ? 'tv' : 'web'} tvNav={tvMode}
          onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
